<template>
  <div class="app-shell">
    <header class="topbar">
      <div class="brand-lockup">
        <div class="brand-mark">P</div>
        <div>
          <div class="brand-name">POOLDESK</div>
          <div class="brand-caption">signal intelligence</div>
        </div>
      </div>
      <div class="topbar-center">
        <span class="topbar-kicker">A-SHARE / LIVE MONITOR</span>
        <span class="topbar-divider"></span>
        <span class="topbar-date">{{ marketDate }}</span>
      </div>
      <div class="topbar-actions">
        <span class="market-state">
          <span class="state-dot" :class="{ live: isMarketOpen }"></span>
          {{ marketStatus }}
        </span>
        <button class="icon-button" :class="{ spinning: loading }" type="button" aria-label="刷新" @click="fetchDashboard(true)">
          ↻
        </button>
      </div>
    </header>

    <main class="dashboard">
      <section class="decision-overview" aria-label="今日决策概览">
        <div class="decision-overview-head">
          <div>
            <p class="eyebrow">TODAY / DECISION DESK</p>
            <h1>今日短线决策台</h1>
          </div>
          <div class="view-mode-switch" role="tablist" aria-label="看板模式">
            <button type="button" :class="{ active: viewMode === 'trade' }" @click="selectViewMode('trade')">交易</button>
            <button type="button" :class="{ active: viewMode === 'research' }" @click="selectViewMode('research')">研究</button>
          </div>
        </div>
        <div class="decision-metrics">
          <article>
            <span>市场周期</span>
            <strong>{{ opportunityCycle.state || opportunityMarket.state || '等待数据' }}</strong>
            <small>{{ cyclePositionText }}</small>
          </article>
          <article>
            <span>主线板块</span>
            <strong>{{ mainlineBoards[0] ? mainlineBoards[0].board_name : '待确认' }}</strong>
            <small>{{ mainlineBoardText }}</small>
          </article>
          <article class="decision-positive">
            <span>A级可行动</span>
            <strong>{{ coreActionCount }}</strong>
            <small>主线 · 触发 · 承接</small>
          </article>
          <article class="decision-risk">
            <span>风险预警</span>
            <strong>{{ riskReviewCount }}</strong>
            <small>{{ strategyCalibration.confidence_text || '样本积累中' }}</small>
          </article>
        </div>
        <div v-if="viewMode === 'research'" class="calibration-strip">
          <span>策略基线</span>
          <small>T+3 {{ calibrationSamples }}样本 · 胜率{{ calibrationWinRate }}% · 均值{{ signedPercent(calibrationAverageReturn) }}</small>
          <small v-if="strategyCalibration.excursion">MFE {{ signedPercent(strategyCalibration.excursion.average_mfe_pct || 0) }} · MAE {{ signedPercent(strategyCalibration.excursion.average_mae_pct || 0) }}</small>
        </div>
        <div v-if="opportunityWarnings.length" class="opportunity-warning compact-warning">
          {{ opportunityWarnings.join('；') }}
        </div>
      </section>

      <section class="workspace">
        <aside class="pool-rail">
          <div class="rail-heading">
            <div>
              <p class="eyebrow">{{ viewMode === 'trade' ? 'WORK QUEUE' : 'MODEL SOURCES' }}</p>
              <h2>{{ viewMode === 'trade' ? '工作池' : '模型源' }}</h2>
            </div>
            <span class="rail-count">{{ pools.length }}</span>
          </div>

          <button
            class="pool-item all-pools"
            :class="{ active: activePoolId === 'all' }"
            type="button"
            @click="selectPool('all')"
          >
            <span class="pool-item-mark mark-all">◎</span>
            <span class="pool-item-copy">
              <strong>{{ viewMode === 'trade' ? '全部工作池' : '全部模型' }}</strong>
              <small>{{ uniqueTrackedCount }} 只股票</small>
            </span>
            <span class="pool-item-count">{{ visiblePoolCount }}</span>
          </button>

          <button
            v-for="pool in pools"
            :key="pool.id"
            class="pool-item"
            :class="{ active: activePoolId === pool.id, stale: pool.stale }"
            type="button"
            @click="selectPool(pool.id)"
          >
            <span class="pool-item-mark" :class="poolMarkClass(pool.id)">{{ poolMark(pool.id) }}</span>
            <span class="pool-item-copy">
              <strong>{{ pool.short_label || pool.label }}</strong>
              <small>{{ viewMode === 'trade' ? pool.description : (pool.stale ? '快照较旧' : formatSnapshot(pool.snapshot_at)) }}</small>
            </span>
            <span class="pool-item-count">{{ poolDisplayCount(pool) }}</span>
          </button>

          <div class="rail-note">
            <span class="note-line"></span>
            <p v-if="viewMode === 'trade'">一股一行，按决策状态归入唯一工作池。</p>
            <p v-else>保留七个底层模型和完整研究字段。</p>
          </div>
        </aside>

        <section class="pool-view">
          <div class="view-header">
            <div>
              <div class="view-kicker">
                <span class="active-pool-dot"></span>
                {{ activePoolLabel }}
              </div>
              <h2>{{ activePoolDescription }}</h2>
            </div>
            <div class="view-controls">
              <label class="search-box">
                <span>⌕</span>
                <input v-model.trim="searchText" type="search" placeholder="搜索名称 / 代码">
              </label>
              <button class="ghost-button" :class="{ selected: positiveOnly }" type="button" @click="positiveOnly = !positiveOnly">
                <span class="filter-glyph">↗</span> 只看上涨
              </button>
              <label class="refresh-control">
                <span>自动刷新</span>
                <select v-model.number="refreshSeconds" @change="restartTimer">
                  <option :value="0">关闭</option>
                  <option :value="15">15秒</option>
                  <option :value="30">30秒</option>
                  <option :value="60">60秒</option>
                </select>
              </label>
            </div>
          </div>

          <div v-if="viewMode === 'research'" class="subtabs" role="tablist">
            <button
              class="subtab"
              :class="{ active: activeStage === '全部' }"
              type="button"
              @click="activeStage = '全部'"
            >
              全部 <span>{{ activePoolUniqueCount }}只</span>
            </button>
            <template v-if="activePoolId === 'stage_model'">
              <div v-for="group in stageTabGroups" :key="group.label" class="subtab-group">
                <span class="subtab-group-label">{{ group.label }}</span>
                <button
                  v-for="stage in group.stages"
                  :key="stage"
                  class="subtab"
                  :class="{ active: activeStage === stage }"
                  type="button"
                  @click="activeStage = stage"
                >
                  {{ stage }} <span :title="stageTabCountTitle(stage)">{{ stageTabCountLabel(stage) }}</span>
                </button>
              </div>
            </template>
            <button
              v-else
              v-for="stage in stageTabs"
              :key="stage"
              class="subtab"
              :class="{ active: activeStage === stage }"
              type="button"
              @click="activeStage = stage"
            >
              {{ stage }} <span :title="stageTabCountTitle(stage)">{{ stageTabCountLabel(stage) }}</span>
            </button>
          </div>
          <div v-if="viewMode === 'research'" class="count-legend">
            <template v-if="activePoolId === 'all'">
              跨池去重后 {{ activePoolUniqueCount }} 只股票 · 各池合计 {{ activePoolRecordCount }} 条信号记录
            </template>
            <template v-else>
              {{ activePoolUniqueCount }} 只股票
            </template>
            · 标签计数按股票去重，标签间可重叠
          </div>
          <div v-else class="count-legend decision-legend">
            {{ activePoolUniqueCount }} 只股票 · 每只股票只保留最高优先级决策状态
          </div>

          <div v-if="activeConfirmationNotice" class="notice-bar confirmation-notice">
            <span>i</span>
            <div>
              <strong>承接确认状态</strong>
              <p>{{ activeConfirmationNotice }}</p>
            </div>
          </div>

          <div v-if="error" class="notice-bar error-notice">
            <span>!</span>
            <div>
              <strong>行情聚合接口暂不可用</strong>
              <p>{{ error }}。页面保留本次已加载的数据。</p>
            </div>
            <button type="button" @click="fetchDashboard(true)">重试</button>
          </div>

          <div class="table-card">
            <div class="table-head">
              <span class="head-rank">#</span>
              <span>标的 / {{ viewMode === 'trade' ? '模型来源' : '信号阶段' }}</span>
              <span class="head-price">
                <button
                  class="sort-button"
                  :class="{ active: sortKey === 'price' }"
                  type="button"
                  :aria-label="`按现价${sortKey === 'price' && sortDirection === 'asc' ? '升序' : '降序'}排序`"
                  @click="sortBy('price')"
                >
                  现价 <span class="sort-indicator">{{ sortIndicator('price') }}</span>
                </button>
                <button
                  class="sort-button"
                  :class="{ active: sortKey === 'change_pct' }"
                  type="button"
                  :aria-label="`按涨跌幅${sortKey === 'change_pct' && sortDirection === 'asc' ? '升序' : '降序'}排序`"
                  @click="sortBy('change_pct')"
                >
                  涨跌 <span class="sort-indicator">{{ sortIndicator('change_pct') }}</span>
                </button>
              </span>
              <span class="head-score">
                <button
                  class="sort-button"
                  :class="{ active: sortKey === 'score' }"
                  type="button"
                  :aria-label="`按评分${sortKey === 'score' && sortDirection === 'asc' ? '升序' : '降序'}排序`"
                  @click="sortBy('score')"
                >
                  {{ viewMode === 'trade' ? '行动' : '评分' }} <span class="sort-indicator">{{ sortIndicator('score') }}</span>
                </button>
              </span>
              <span class="head-sector">{{ viewMode === 'trade' ? '触发 / 承接 / 结构位' : '板块 / 线索 / 结构位' }}</span>
              <span class="head-action">状态</span>
            </div>
            <div v-if="loading && !loaded" class="loading-state">正在加载股池快照…</div>
            <div v-else-if="filteredRows.length === 0" class="empty-state">
              <div class="empty-symbol">∅</div>
              <h3>当前筛选没有标的</h3>
              <p>可以切换股池、阶段标签，或清空搜索条件。</p>
            </div>
            <template v-else>
              <button
                v-for="(row, index) in filteredRows"
                :key="row.pool_id + '-' + row.code + '-' + row.stage"
                class="stock-row"
                :class="{ selected: selectedRow && selectedRow.code === row.code && selectedRow.pool_id === row.pool_id }"
                type="button"
                @click="selectRow(row)"
              >
                <span class="row-rank">{{ String(index + 1).padStart(2, '0') }}</span>
                <span class="stock-identity">
                  <span class="stock-name-line">
                    <strong>{{ row.name || row.code }}</strong>
                    <span v-if="row.role_text" class="role-badge" :class="roleClass(row.role)">{{ row.role_text }}</span>
                  </span>
                  <small>{{ row.code }} · {{ row.setup_type || row.stage || row.pool_label }}</small>
                  <small v-if="row.evidence_total" class="evidence-summary">证据 {{ row.evidence_count }}/{{ row.evidence_total }} · {{ row.board_name || row.sector || row.industry || '主线待确认' }}</small>
                </span>
                <span class="stock-price">
                  <strong>{{ priceText(row.price) }}</strong>
                  <span :class="changeClass(row.change_pct)">{{ signedPercent(row.change_pct) }}</span>
                </span>
                <span class="score-cell">
                  <strong>{{ row.action_grade || scoreText(row.score) }}</strong>
                  <small>{{ row.action_grade ? `行动分 ${scoreText(row.score)}` : (actionReference(row) || row.tier || scoreLabel(row.score)) }}</small>
                </span>
                <span class="signal-cell">
                  <span class="sector-name">{{ row.trigger_text || row.stage || '结构观察' }}</span>
                  <small class="support-signal" :class="supportToneClass(row)">{{ row.support_signal_text || row.support_text || primaryReason(row) }}</small>
                  <small
                    v-if="row.platform_levels && row.platform_levels.available"
                    class="box-row-signal"
                    :class="platformToneClass(row.platform_levels)"
                  >{{ platformListText(row) }}</small>
                </span>
                <span class="row-status">
                  <span class="status-chip" :class="decisionStatusClass(row)">{{ row.decision_state_text || statusText(row) }}</span>
                  <span class="row-chevron">›</span>
                </span>
              </button>
            </template>
            <div v-if="filteredRows.length > 0" class="table-foot">
              显示 {{ filteredRows.length }} 条记录 / {{ filteredUniqueCount }} 只股票 · 当前筛选共 {{ activeViewRecordCount }} 条记录 / {{ activeViewUniqueCount }} 只股票 · 按{{ sortLabel }}排序
            </div>
          </div>
        </section>

        <button
          v-if="detailPanelOpen"
          class="detail-backdrop"
          type="button"
          aria-label="关闭信号详情"
          @click="closeDetailPanel"
        ></button>

        <section class="insight-grid" :class="{ 'detail-open': detailPanelOpen }">
        <article class="insight-card detail-card">
          <div class="card-topline">
            <span class="eyebrow">SELECTED SIGNAL</span>
            <div class="detail-top-actions">
              <span v-if="selectedRow" class="selected-pool">{{ selectedRow.work_pool_label || selectedRow.pool_label }}</span>
              <button class="detail-close" type="button" aria-label="关闭信号详情" @click="closeDetailPanel">×</button>
            </div>
          </div>
          <div v-if="selectedRow" class="selected-content">
            <div class="selected-title">
              <div>
                <h3>{{ selectedRow.name }}</h3>
                <p>{{ selectedRow.code }} · {{ selectedRow.stage || '未分组' }}</p>
              </div>
              <div class="selected-price">
                <strong>{{ priceText(selectedRealtimePrice) }}</strong>
                <span :class="changeClass(selectedRealtimeChange)">{{ signedPercent(selectedRealtimeChange) }}</span>
              </div>
            </div>
            <section class="decision-detail-summary">
              <div><span>决策</span><strong>{{ selectedRow.decision_state_text || statusText(selectedRow) }}</strong></div>
              <div><span>地位</span><strong>{{ selectedRow.role_text || '地位待确认' }}</strong></div>
              <div><span>触发</span><strong>{{ selectedRow.trigger_text || selectedRow.stage || '等待触发' }}</strong></div>
              <div class="decision-invalid"><span>失效条件</span><strong>{{ selectedRow.invalid_condition || '原模型结构失效' }}</strong></div>
            </section>
            <section class="support-strip" :class="supportToneClass(selectedSupport)">
              <div class="support-strip-head">
                <div>
                  <span>FQGate 实时承接</span>
                  <small>{{ selectedSupport.freshness }} · {{ selectedSupport.confidence }}</small>
                </div>
                <strong>{{ selectedSupport.signal }}</strong>
              </div>
              <div class="support-strip-grid">
                <div><span>即时</span><strong>{{ scoreText(selectedSupport.score) }}</strong></div>
                <div><span>滚动</span><strong>{{ scoreText(selectedSupport.rollingScore) }}</strong></div>
                <div><span>盘口</span><strong>{{ ratioText(selectedSupport.bookImbalance, true) }}</strong></div>
                <div><span>主动买</span><strong>{{ ratioText(selectedSupport.activeBuyRatio, false) }}</strong></div>
                <div><span>覆盖</span><strong>{{ selectedSupport.coverage ? `${selectedSupport.coverage}项` : '--' }}</strong></div>
              </div>
              <small class="support-strip-updated">{{ selectedSupport.updatedAt || '等待实时承接数据' }}</small>
            </section>
            <div v-if="selectedRow.evidence_items && selectedRow.evidence_items.length" class="evidence-matrix">
              <span
                v-for="item in selectedRow.evidence_items"
                :key="item.key"
                :class="{ passed: item.passed, unknown: item.unknown }"
              >{{ item.label }} · {{ item.passed ? '通过' : (item.unknown ? '待补' : '未通过') }}</span>
            </div>
            <div v-if="viewMode === 'research'" class="selected-metrics">
              <div><span>评分</span><strong>{{ scoreText(selectedRow.score) }}</strong></div>
              <div><span>行动参考</span><strong>{{ actionReference(selectedRow) || '未分层' }}</strong></div>
              <template v-if="isStageModelSelected">
                <div><span>当前阶段进入日</span><strong>{{ currentStagePhase.interval_start || '--' }}</strong></div>
                <div><span>最近确认日</span><strong>{{ currentStagePhase.last_confirmed_date || selectedRow.tracking_last_signal_date || '--' }}</strong></div>
                <div><span>阶段持续</span><strong>{{ currentStagePhase.inferred_bars || (Number(selectedRow.tracking_age_bars || 0) + 1) }} 个交易日</strong></div>
              </template>
              <div v-if="isStageModelSelected && selectedRow.tdx_channel && selectedRow.tdx_channel.trade_date">
                <span>通达信通道 · {{ selectedRow.tdx_channel.trade_date }}</span>
                <strong>{{ selectedRow.tdx_channel_not_gun ? '未出通道' : '未满足/数据不足' }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.tdx_channel && selectedRow.tdx_channel.lower > 0">
                <span>MA10通道下轨 / 上轨</span>
                <strong>{{ priceText(selectedRow.tdx_channel.lower) }} / {{ priceText(selectedRow.tdx_channel.upper) }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_peak_detail && selectedRow.chip_peak_detail.trade_date">
                <span>筹码峰 · {{ selectedRow.chip_peak_detail.trade_date }}</span>
                <strong>{{ priceText(selectedRow.chip_peak_detail.peak_price) }} · 较峰 {{ signedPercent(selectedRow.chip_peak_detail.distance_pct) }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_peak_detail && selectedRow.chip_peak_detail.trade_date">
                <span title="原字段 chip_concentration_pct，实际含义是15%至85%筹码成本区间的相对带宽；越低越集中">筹码成本带宽</span><strong>{{ unsignedPercentOrDash(chipBandWidth(selectedRow.chip_peak_detail)) }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_peak_detail && selectedRow.chip_peak_detail.trade_date">
                <span title="展示评分=100×(1-成本带宽/12%)，限制在0至100；只用于直观展示，不改变选股门槛">筹码密集度评分</span><strong>{{ densityScoreText(chipDensityScore(selectedRow.chip_peak_detail)) }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_peak_detail && selectedRow.chip_peak_detail.trade_date">
                <span>带宽较昨日 / 3日</span>
                <strong>{{ signedPointsOrDash(selectedRow.chip_peak_detail.width_change_1d_pct_points) }} / {{ signedPointsOrDash(selectedRow.chip_peak_detail.width_change_3d_pct_points) }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_peak_detail && selectedRow.chip_peak_detail.trade_date">
                <span>筹码趋势状态</span><strong>{{ selectedRow.chip_peak_detail.trend_state || '观察中' }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_trend_t5_detail && selectedRow.chip_trend_t5_detail.trade_date">
                <span>T+5观察条件</span>
                <strong>{{ selectedRow.chip_trend_t5 ? '基础条件满足' : '未满足' }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_trend_t5_detail && selectedRow.chip_trend_t5_detail.trade_date">
                <span>峰距 / 成本带宽</span>
                <strong>{{ signedPercent(selectedRow.chip_trend_t5_detail.distance_pct) }} / {{ unsignedPercentOrDash(chipBandWidth(selectedRow.chip_trend_t5_detail)) }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_trend_t5_detail && selectedRow.chip_trend_t5_detail.trade_date">
                <span>盘中参与状态</span>
                <strong>{{ selectedRow.chip_trend_t5_participation_ready ? '盘中可参与' : '等待条件' }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_trend_t5_detail && selectedRow.chip_trend_t5_detail.market_breadth_pct !== undefined">
                <span>日内位置 / 市场宽度</span>
                <strong>{{ percentOrDash(Number(selectedRow.chip_trend_t5_detail.intraday_position || 0) * 100) }} / {{ percentOrDash(selectedRow.chip_trend_t5_detail.market_breadth_pct) }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.bullish_oversold">
                <span>多头超跌初筛</span>
                <strong>收盘条件满足</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.bullish_oversold_detail && selectedRow.bullish_oversold_detail.trade_date">
                <span>MA13 / MA17 / MA55</span>
                <strong>{{ priceText(selectedRow.bullish_oversold_detail.ma13) }} / {{ priceText(selectedRow.bullish_oversold_detail.ma17) }} / {{ priceText(selectedRow.bullish_oversold_detail.ma55) }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.bullish_oversold_detail && selectedRow.bullish_oversold_detail.trade_date">
                <span>均线粘合 / 筹码离散</span>
                <strong>{{ percentOrDash(selectedRow.bullish_oversold_detail.ma_spread_pct) }} / {{ percentOrDash(selectedRow.bullish_oversold_detail.chip_concentration_pct) }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.bullish_oversold_detail && selectedRow.bullish_oversold_detail.trade_date">
                <span>筹码峰 / 因果下轨</span>
                <strong>{{ priceText(selectedRow.bullish_oversold_detail.chip_peak_price) }} / {{ priceText(selectedRow.bullish_oversold_detail.causal_lower) }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.bullish_oversold">
                <span>盘中承接状态</span>
                <strong>{{ selectedRow.bullish_oversold_confirmation_ready ? '承接确认' : '等待承接' }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.signal_tier">
                <span>信号分层 / 信号日</span>
                <strong>{{ selectedRow.signal_tier }} / {{ selectedRow.signal_trade_date || '--' }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.lifecycle">
                <span>生命周期</span>
                <strong>{{ selectedRow.tracking_status || '跟踪中' }} · T+{{ selectedRow.tracking_age_bars || 0 }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.checkpoints && Object.keys(selectedRow.checkpoints).length">
                <span>T+3 / T+5 / T+10</span>
                <strong>
                  {{ selectedRow.checkpoints['3d_close'] ? signedPercent(selectedRow.checkpoints['3d_close'].pct) : '--' }} /
                  {{ selectedRow.checkpoints['5d_close'] ? signedPercent(selectedRow.checkpoints['5d_close'].pct) : '--' }} /
                  {{ selectedRow.checkpoints['10d_close'] ? signedPercent(selectedRow.checkpoints['10d_close'].pct) : '--' }}
                </strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.checkpoints && Object.keys(selectedRow.checkpoints).length">
                <span>同期全A等权超额</span>
                <strong>
                  {{ selectedRow.checkpoints['3d_close'] && selectedRow.checkpoints['3d_close'].excess_pct !== null ? signedPercent(selectedRow.checkpoints['3d_close'].excess_pct) : '--' }} /
                  {{ selectedRow.checkpoints['5d_close'] && selectedRow.checkpoints['5d_close'].excess_pct !== null ? signedPercent(selectedRow.checkpoints['5d_close'].excess_pct) : '--' }} /
                  {{ selectedRow.checkpoints['10d_close'] && selectedRow.checkpoints['10d_close'].excess_pct !== null ? signedPercent(selectedRow.checkpoints['10d_close'].excess_pct) : '--' }}
                </strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.bullish_oversold_detail && selectedRow.bullish_oversold_detail.market_breadth_pct !== undefined">
                <span>日内位置 / 市场宽度 / 开盘缺口</span>
                <strong>{{ percentOrDash(Number(selectedRow.bullish_oversold_detail.intraday_position || 0) * 100) }} / {{ percentOrDash(selectedRow.bullish_oversold_detail.market_breadth_pct) }} / {{ percentOrDash(selectedRow.bullish_oversold_detail.open_gap_pct) }}</strong>
              </div>
              <div v-if="isFirstBoardSelected && selectedRow.signal_tier">
                <span>信号分层 / 信号日</span>
                <strong>{{ selectedRow.signal_tier }} / {{ selectedRow.signal_trade_date || '--' }}</strong>
              </div>
              <div v-if="isFirstBoardSelected && selectedRow.auction_amount_yuan">
                <span>竞价额 / 昨日成交额</span>
                <strong>{{ amountText(selectedRow.auction_amount_yuan) }} / {{ percentOrDash(selectedRow.auction_amount_to_previous_pct) }}</strong>
              </div>
              <div v-if="isFirstBoardSelected && selectedRow.auction_amount_to_float_bps">
                <span>竞价额/流通市值 · 买卖盘比</span>
                <strong>{{ Number(selectedRow.auction_amount_to_float_bps).toFixed(2) }}bp / {{ selectedRow.auction_bid_ask_ratio ? Number(selectedRow.auction_bid_ask_ratio).toFixed(2) : '--' }}</strong>
              </div>
              <div v-if="isFirstBoardSelected && selectedRow.auction_sector_breadth_pct">
                <span>板块上涨宽度 / 平均涨幅</span>
                <strong>{{ percentOrDash(selectedRow.auction_sector_breadth_pct) }} / {{ percentOrDash(selectedRow.auction_sector_average_change_pct) }}</strong>
              </div>
              <div v-if="isFirstBoardSelected && selectedRow.lifecycle && selectedRow.lifecycle.outcome_ready">
                <span>结果 · {{ selectedRow.outcome_trade_date || '--' }}</span>
                <strong>{{ selectedRow.is_first_board ? '已封首板' : '炸板/未封' }} · 开板{{ selectedRow.open_times || 0 }}次</strong>
              </div>
              <div><span>竞价涨幅</span><strong>{{ percentOrDash(selectedRow.auction_change_pct) }}</strong></div>
              <div><span>较竞价</span><strong>{{ percentOrDash(selectedRow.current_vs_auction_pct) }}</strong></div>
              <div v-if="selectedRow.auction_state"><span>竞价状态机</span><strong>{{ selectedRow.auction_state_text }}</strong></div>
              <div v-if="selectedRow.market_cycle_state"><span>市场周期 / 仓位</span><strong>{{ selectedRow.market_cycle_state }} · {{ selectedRow.position_recommendation }}</strong></div>
              <div><span>行情源</span><strong>{{ selectedRow.quote_status === 'live' ? '实时' : '快照' }}</strong></div>
              <div v-if="isLiveActionPool(selectedRow) && selectedRow.intraday_action_score !== null && selectedRow.intraday_action_score !== undefined">
                <span>实时行动分 / 数据质量</span>
                <strong>{{ scoreText(selectedRow.intraday_action_score) }} / {{ selectedRow.intraday_action_data_quality === 'full' ? '行情+L2' : selectedRow.intraday_action_data_quality === 'quote_only' ? '仅行情' : '形态' }}</strong>
              </div>
            </div>
            <section v-if="viewMode === 'research' && hasSelectedL2Factor" class="l2-panel" :class="l2StatusClass(selectedL2Factor.support_status || selectedL2Factor.stage_status || selectedL2Factor.status)">
              <div class="l2-panel-head">
                <div>
                  <span>FQGate 实时承接</span>
                  <small>{{ l2Watch.state === 'live' ? '实时盯盘' : l2StateText }}</small>
                </div>
                <strong>{{ selectedL2Factor.support_status_text || selectedL2Factor.stage_status_text || selectedL2Factor.status_text || '等待数据' }}</strong>
              </div>
              <p class="l2-stage-note">{{ selectedL2Factor.stage_note || '盘口、委托队列、撤单、主动成交及资金流仅在可解释字段到达后参与承接评分。' }}</p>
              <div class="l2-factor-grid">
                <div><span>承接评分</span><strong>{{ Number(selectedL2Factor.support_score || selectedL2Factor.score || 0).toFixed(1) }}</strong></div>
                <div><span>十档盘口</span><strong>{{ ratioText(selectedL2Factor.book_imbalance, true) }}</strong></div>
                <div><span>委托队列</span><strong>{{ ratioText(selectedL2Factor.queue_imbalance, true) }}</strong></div>
                <div><span>撤单偏向</span><strong>{{ ratioText(selectedL2Factor.cancellation_imbalance, true) }}</strong></div>
                <div><span>主动买入比</span><strong>{{ ratioText(selectedL2Factor.active_buy_ratio, false) }}</strong></div>
                <div><span>个股资金净流入</span><strong>{{ amountText((selectedL2Factor.capital_flow || {}).net_inflow_yuan) }}</strong></div>
                <div><span>资金流占比</span><strong>{{ percentOrDash((selectedL2Factor.capital_flow || {}).net_inflow_ratio_pct) }}</strong></div>
                <div><span>大单净流入</span><strong>{{ amountText((selectedL2Factor.big_order_flow || {}).net_inflow_yuan) }}</strong></div>
                <div><span>大单流占比</span><strong>{{ percentOrDash((selectedL2Factor.big_order_flow || {}).net_inflow_ratio_pct) }}</strong></div>
                <div v-if="selectedL2Factor.support_window"><span>低点修复</span><strong>{{ percentOrDash(selectedL2Factor.support_window.support_low_reclaim_pct) }}</strong></div>
                <div v-if="selectedL2Factor.support_window"><span>卖压持续</span><strong>{{ Number(selectedL2Factor.support_window.active_sell_persistence || 0).toFixed(1) }}</strong></div>
                <div v-if="selectedL2Factor.support_window"><span>队列撤单质量</span><strong>{{ ratioText(selectedL2Factor.support_window.queue_cancel_quality, true) }}</strong></div>
                <div v-if="selectedL2Factor.support_window"><span>承接衰减分</span><strong>{{ Number(selectedL2Factor.support_window.support_decay_score || 0).toFixed(1) }}</strong></div>
              </div>
              <small v-if="selectedL2Factor.component_names && selectedL2Factor.component_names.length" class="l2-components">评分覆盖：{{ selectedL2Factor.component_names.join(' · ') }}</small>
              <div class="l2-streams">
                <span
                  v-for="kind in l2StreamKinds"
                  :key="kind"
                  :class="{ online: l2Watch.streams[kind] === 'connected', failed: l2Watch.streams[kind] === 'error' }"
                >{{ l2KindLabel(kind) }} · {{ l2EventCount(kind) }}</span>
              </div>
              <small v-if="l2Watch.error" class="l2-error">{{ l2Watch.error }}</small>
              <small v-else class="l2-updated">{{ selectedL2Factor.updated_at || '等待可解释字段' }}</small>
            </section>
            <section v-if="viewMode === 'research' && isStageModelSelected" class="stage-timeline-panel">
              <div class="stage-timeline-head">
                <strong>量价阶段时间轴</strong>
                <span>{{ stageTimelineLoading ? '计算中' : (stageTimeline.as_of_date || '') }}</span>
              </div>
              <p v-if="stageTimeline.error" class="stage-timeline-message">{{ stageTimeline.error }}</p>
              <p v-else-if="!stageTimeline.cycles || !stageTimeline.cycles.length" class="stage-timeline-message">
                {{ stageTimelineLoading ? '正在读取该标的日线并回放阶段' : '尚无可回放的完整阶段周期' }}
              </p>
              <div v-else class="stage-cycle-list">
                <article v-for="cycle in stageTimeline.cycles" :key="cycle.cycle_id" class="stage-cycle">
                  <div class="stage-cycle-heading">
                    <strong>周期 {{ cycle.cycle_id }}</strong>
                    <span>{{ cycle.start_date }} ~ {{ cycle.end_date }} · {{ cycle.status }}</span>
                  </div>
                  <div v-for="(phase, index) in cycle.phases" :key="`${cycle.cycle_id}-${index}`" class="stage-phase-row">
                    <strong>{{ phase.stage }}</strong>
                    <span>区间 {{ phase.interval_start || '--' }} ~ {{ phase.interval_end || '--' }}</span>
                    <span>确认 {{ phase.first_confirmed_date || '--' }}<template v-if="phase.last_confirmed_date && phase.last_confirmed_date !== phase.first_confirmed_date"> ~ {{ phase.last_confirmed_date }}</template></span>
                    <small>{{ phase.status || '状态未知' }} · 确认 {{ phase.confirmed_bars }} 日 / 推断 {{ phase.inferred_bars }} 日</small>
                  </div>
                </article>
              </div>
              <small v-if="stageTimeline.method_note" class="stage-timeline-note">{{ stageTimeline.method_note }}</small>
            </section>
            <div
              v-if="viewMode === 'research' && stageTrackingText(selectedRow)"
              class="selected-stage-state"
              :class="stageTrackingClass(selectedRow)"
            >
              <span>当前跟踪状态</span>
              <strong>{{ stageTrackingText(selectedRow) }}</strong>
              <small>正式阶段与评分仍以收盘日线确认；盘中状态仅用于跟踪。</small>
            </div>
            <div v-if="viewMode === 'research'" class="daily-position-panel">
              <div class="daily-position-head">
                <span>日线位置</span>
                <small>
                  {{ selectedDailyPositions.trade_date || '--' }}
                  · {{ selectedDailyPositions.daily_bar_incomplete ? '盘中合成日K' : (selectedDailyPositions.available ? '正式日线' : '暂无数据') }}
                </small>
              </div>
              <div class="daily-ma-grid">
                <div
                  v-for="item in dailyMaRows"
                  :key="item.key"
                  class="daily-ma-item"
                  :class="dailyMaClass(item.key)"
                >
                  <span>{{ item.label }}</span>
                  <strong>{{ priceText(selectedDailyPositions[item.key]) }}</strong>
                  <small>
                    {{ !selectedDailyPositions.available ? '暂无日线' : (dailyMaTouched(item.key) ? (dailyMaBelow(item.key) ? '日内回踩后仍在下方' : '日内回踩后收回') : '今日未回踩') }}
                  </small>
                </div>
              </div>
              <div class="daily-range">
                <div>
                  <span>日内最高</span>
                  <strong>{{ priceText(selectedDailyPositions.intraday_high) }}</strong>
                </div>
                <div>
                  <span>日内最低</span>
                  <strong>{{ priceText(selectedDailyPositions.intraday_low) }}</strong>
                </div>
                <small>
                  {{ selectedDailyPositions.intraday_source === 'tdx_realtime_quote' ? '实时行情' : '日线快照' }}
                </small>
              </div>
            </div>
            <div v-if="viewMode === 'research'" class="ma-cross-panel">
              <div class="ma-cross-panel-head">
                <span>均线信号状态</span>
                <small>MA13/MA40 与 MA55</small>
              </div>
              <div v-if="selectedMaCrossSignals.length" class="ma-cross-detail-list">
                <div
                  v-for="signal in selectedMaCrossSignals"
                  :key="signal.label"
                  class="ma-cross-detail"
                  :class="maCrossClass(signal)"
                >
                  <strong>{{ signal.label }}</strong>
                  <span>{{ signal.state }}</span>
                  <small>快线 {{ priceText(signal.fast_ma) }} · MA55 {{ priceText(signal.slow_ma) }}</small>
                </div>
              </div>
              <div v-else class="ma-cross-empty">当前未触发金叉状态</div>
            </div>
            <div v-if="selectedRow.reasons && selectedRow.reasons.length" class="reason-list">
              <span v-for="reason in selectedRow.reasons.slice(0, 3)" :key="reason">{{ reason }}</span>
            </div>
            <div
              v-if="selectedPlatformLevels.available"
              class="platform-levels"
              :class="platformToneClass(selectedPlatformLevels)"
            >
              <div class="platform-levels-head">
                <span>{{ platformTitle(selectedPlatformLevels) }}</span>
                <small>{{ selectedPlatformLevels.source }} · {{ selectedPlatformLevels.start_date || '--' }} ~ {{ selectedPlatformLevels.end_date || '--' }}</small>
              </div>
              <div class="platform-state-line">
                <strong>{{ selectedPlatformLevels.state }}</strong>
                <span>{{ platformDistanceText(selectedPlatformLevels) }}</span>
              </div>
              <div class="platform-track" aria-hidden="true">
                <span class="platform-track-fill"></span>
                <i class="platform-track-marker" :style="selectedPlatformMarkerStyle"></i>
              </div>
              <div class="platform-level-grid">
                <div class="platform-level defense-level">
                  <div class="platform-level-label">{{ platformDefenseLabel(selectedPlatformLevels) }}</div>
                  <strong>{{ priceText(selectedPlatformLevels.defense_price) }}</strong>
                  <small>防守 {{ signedPercent(selectedPlatformLevels.distance_to_defense_pct) }}</small>
                </div>
                <div class="platform-level current-level">
                  <div class="platform-level-label">当前</div>
                  <strong>{{ priceText(selectedRealtimePrice) }}</strong>
                  <small>位置 {{ platformPositionText(selectedPlatformLevels) }}</small>
                </div>
                <div class="platform-level attack-level">
                  <div class="platform-level-label">{{ platformAttackLabel(selectedPlatformLevels) }}</div>
                  <strong>{{ priceText(selectedPlatformLevels.attack_price) }}</strong>
                  <small>{{ platformDistanceText(selectedPlatformLevels) }}</small>
                </div>
              </div>
              <div v-if="selectedPlatformLevels.box_kind === 'n_breakout'" class="platform-structure-metrics">
                <span>启动L1 <strong>{{ priceText(selectedPlatformLevels.origin_price) }}</strong></span>
                <span>第一波 <strong>{{ signedPercent(selectedPlatformLevels.impulse_pct) }}</strong></span>
                <span>回踩 <strong>{{ Number(selectedPlatformLevels.retracement_pct || 0).toFixed(1) }}%</strong></span>
                <span>缩量比 <strong>{{ Number(selectedPlatformLevels.pullback_volume_ratio || 0).toFixed(2) }}</strong></span>
              </div>
              <div class="platform-rules">
                <span>{{ selectedPlatformLevels.attack_rule }}</span>
                <span>{{ selectedPlatformLevels.defense_rule }}</span>
                <span v-if="Number(selectedPlatformLevels.breakout_volume_ratio) > 0">突破量比 {{ Number(selectedPlatformLevels.breakout_volume_ratio).toFixed(2) }}</span>
              </div>
            </div>
            <div v-if="selectedRow.risks && selectedRow.risks.length" class="risk-line">风险：{{ selectedRow.risks.join('；') }}</div>
          </div>
          <div v-else class="placeholder-detail">
            <div class="placeholder-mark">↗</div>
            <h3>选择一个标的</h3>
            <p>点击上方列表查看完整信号、竞价和当前价格对比。</p>
          </div>
        </article>

        <article class="insight-card pulse-card">
          <div class="card-topline">
            <span class="eyebrow">MARKET PULSE</span>
            <span class="pulse-time">{{ lastUpdatedText }}</span>
          </div>
          <div class="pulse-title">
            <h3>盘面脉搏</h3>
            <span class="pulse-badge">{{ positiveRatio }}% ↑</span>
          </div>
          <div class="bar-list">
            <div v-for="item in pulseRows" :key="item.label" class="bar-item">
              <div class="bar-label"><span>{{ item.label }}</span><strong>{{ item.count }}</strong></div>
              <div class="bar-track"><span :style="{ width: item.percent + '%' }" :class="item.tone"></span></div>
            </div>
          </div>
          <section class="fqgate-radar" aria-label="FQGate市场雷达">
            <div class="fqgate-radar-head">
              <div>
                <span>FQGATE MARKET RADAR</span>
                <small>{{ fqgateStatusText }}</small>
              </div>
              <strong>{{ (fqgateOverview.data_updated_at || fqgateOverview.updated_at) ? (fqgateOverview.data_updated_at || fqgateOverview.updated_at).slice(11, 19) : '--:--:--' }}</strong>
            </div>
            <div class="fqgate-overview-grid">
              <div v-for="item in fqgateOverviewRows" :key="item.key">
                <span>{{ item.label }}</span><strong>{{ item.value }}</strong>
              </div>
            </div>
            <div v-if="fqgateAlerts.length" class="fqgate-alert-list">
              <div v-for="(alert, index) in fqgateAlerts.slice(0, 5)" :key="`${alert.received_at}-${index}`" class="fqgate-alert-row">
                <span>{{ alert.received_at ? alert.received_at.slice(11, 19) : '--:--:--' }}</span>
                <strong>{{ alertText(alert.event) }}</strong>
              </div>
            </div>
            <small v-else class="fqgate-empty">{{ fqgateAlertState === 'connecting' ? '正在连接短线异动流' : '暂无短线异动事件' }}</small>
          </section>
          <p class="pulse-note">阶段模型池按阶段拆分显示；其它股池保留原始池层标签。</p>
        </article>
        </section>
      </section>

      <footer class="dashboard-footer">
        <span>POOLDESK / 本地项目股池观察台</span>
        <span>快照 {{ snapshotUpdatedText }} · 行情 {{ quoteUpdatedText }}</span>
      </footer>
    </main>
  </div>
</template>

<script>
const API_BASE = (process.env.VUE_APP_BASE_API || '').replace(/\/$/, '')
const STAGE_MODEL_FILTER_TABS = [
  '短周期跟踪',
  '长周期观察池',
  '优先关注',
  '未出通道',
  '靠近主成本区',
  '筹码持续收敛',
  '筹码重新发散',
  '收敛但价格偏离',
  '筹码峰下移',
  'T+5观察条件',
  '建仓吸筹',
  '试盘突破',
  '洗盘企稳',
  '拉升跟踪',
  '风险回避'
]
const STAGE_MODEL_PHASE_TABS = [
  '建仓吸筹',
  '试盘突破',
  '洗盘企稳',
  '拉升跟踪'
]
const STAGE_MODEL_TAB_GROUPS = [
  { label: '阶段', stages: STAGE_MODEL_PHASE_TABS },
  { label: '跟踪层级', stages: ['短周期跟踪', '长周期观察池'] },
  { label: '关注与风险', stages: ['优先关注', '风险回避'] },
  { label: '结构因子', stages: ['未出通道', '靠近主成本区', '筹码持续收敛', '筹码重新发散', '收敛但价格偏离', '筹码峰下移', 'T+5观察条件'] }
]

export default {
  name: 'App',
  data () {
    return {
      dashboard: { pools: [] },
      loaded: false,
      viewMode: 'trade',
      activePoolId: 'all',
      activeStage: '全部',
      searchText: '',
      positiveOnly: false,
      selectedRow: null,
      detailPanelOpen: false,
      selectedOpportunityBoard: '',
      fqgateMarket: { status: 'idle', boards: [], memberships: {}, quotes: {}, error: '' },
      fqgateRequestId: 0,
      fqgateStatus: { status: 'idle', configured: false, stream_kinds: [] },
      fqgateOverview: { status: 'idle', feeds: {}, errors: {}, coverage: {}, updated_at: '' },
      fqgateAlerts: [],
      fqgateAlertSocket: null,
      fqgateAlertReconnectTimer: null,
      fqgateAlertState: 'idle',
      stageTimeline: { cycles: [] },
      stageTimelineLoading: false,
      stageTimelineRequestId: 0,
      sortKey: 'score',
      sortDirection: 'desc',
      refreshSeconds: 30,
      loading: false,
      error: '',
      refreshTimer: null,
      l2Socket: null,
      l2ReconnectTimer: null,
      l2Watch: {
        state: 'idle',
        factor: {},
        quote: {},
        streams: {},
        events: [],
        error: ''
      }
    }
  },
  computed: {
    rawPools () {
      return Array.isArray(this.dashboard.pools) ? this.dashboard.pools : []
    },
    workPools () {
      return Array.isArray(this.dashboard.work_pools) ? this.dashboard.work_pools : []
    },
    pools () {
      return this.viewMode === 'trade' && this.workPools.length ? this.workPools : this.rawPools
    },
    strategyCalibration () {
      return this.dashboard.strategy_calibration && typeof this.dashboard.strategy_calibration === 'object'
        ? this.dashboard.strategy_calibration
        : {}
    },
    mainlineBoards () {
      return this.opportunityBoards.filter(board => board.state === '资金扩散确认' || board.direction === 'inflow').slice(0, 3)
    },
    mainlineBoardText () {
      const names = this.mainlineBoards.map(board => board.board_name).filter(Boolean)
      return names.length ? names.join(' · ') : '资金扩散尚未确认'
    },
    cyclePositionText () {
      const policy = this.opportunityCycle.risk_policy || {}
      if (!Object.keys(policy).length) return '仓位等待周期确认'
      return `${policy.position_recommendation || '观察'} · 上限${Number(policy.max_position_pct || 0).toFixed(0)}%`
    },
    coreActionCount () {
      const pool = this.workPools.find(item => item.id === 'core_action')
      return Number((pool && pool.total_count) || 0)
    },
    riskReviewCount () {
      const pool = this.workPools.find(item => item.id === 'risk_review')
      return Number((pool && pool.total_count) || 0)
    },
    calibrationSamples () {
      return Number(this.strategyCalibration.samples || 0)
    },
    calibrationWinRate () {
      return Number((this.strategyCalibration.t3 || {}).win_rate_pct || 0).toFixed(1)
    },
    calibrationAverageReturn () {
      return Number((this.strategyCalibration.t3 || {}).average_return_pct || 0)
    },
    opportunityRadar () {
      return this.dashboard.opportunity_radar && typeof this.dashboard.opportunity_radar === 'object'
        ? this.dashboard.opportunity_radar
        : {}
    },
    opportunityMarket () {
      return this.opportunityRadar.market && typeof this.opportunityRadar.market === 'object'
        ? this.opportunityRadar.market
        : {}
    },
    opportunityCycle () {
      return this.opportunityMarket.cycle && typeof this.opportunityMarket.cycle === 'object'
        ? this.opportunityMarket.cycle
        : {}
    },
    opportunitySummary () {
      return this.opportunityRadar.summary && typeof this.opportunityRadar.summary === 'object'
        ? this.opportunityRadar.summary
        : {}
    },
    opportunityBoards () {
      return Array.isArray(this.opportunityRadar.boards) ? this.opportunityRadar.boards : []
    },
    opportunityStocks () {
      const stocks = Array.isArray(this.opportunityRadar.stocks) ? this.opportunityRadar.stocks : []
      if (!this.selectedOpportunityBoard) return stocks
      return stocks.filter(stock => stock.board_name === this.selectedOpportunityBoard)
    },
    opportunityWarnings () {
      const warnings = Array.isArray(this.opportunityRadar.warnings) ? [...this.opportunityRadar.warnings] : []
      return warnings
    },
    opportunityEvaluation () {
      return this.opportunityRadar.evaluation && typeof this.opportunityRadar.evaluation === 'object'
        ? this.opportunityRadar.evaluation
        : {}
    },
    activePool () {
      return this.pools.find(pool => pool.id === this.activePoolId) || null
    },
    activeConfirmationNotice () {
      if (this.viewMode !== 'research') return ''
      if (!this.activePool || !['bullish_oversold', 'first_board_discovery'].includes(this.activePool.id)) return ''
      const status = this.activePool.confirmation_status || {}
      const readiness = status.auction_readiness || {}
      const diagnostics = status.diagnostics || {}
      if (readiness.ready === false) return readiness.error || diagnostics.message || '竞价数据尚未就绪，任务仍在重试。'
      const evaluated = Number(diagnostics.evaluated || diagnostics.eligible_count || 0)
      const confirmed = Number(diagnostics.confirmed_count || 0)
      if (evaluated > 0 && confirmed === 0) {
        const labels = {
          change_pct: '涨跌幅',
          above_open: '站上开盘价',
          intraday_position: '日内位置',
          market_breadth: '市场宽度',
          open_gap: '开盘缺口',
          holds_causal_lower: '守住因果下轨',
          auction_gap: '竞价涨幅',
          auction_amount: '竞价额',
          amount_to_previous: '竞价额/昨额',
          amount_to_float: '竞价额/流通市值',
          above_defense: '防守位',
          auction_score: '综合分'
        }
        const failed = diagnostics.failed_checks || diagnostics.rejected_by_gate || {}
        const summary = Object.keys(failed)
          .sort((left, right) => Number(failed[right]) - Number(failed[left]))
          .slice(0, 4)
          .map(key => `${labels[key] || key}${failed[key]}只`)
          .join('、')
        return `已评估${evaluated}只，本轮无承接确认${summary ? `；主要未通过：${summary}` : ''}。`
      }
      if (evaluated === 0) {
        return this.activePool.id === 'bullish_oversold'
          ? '尚未生成今日盘中承接评估；请检查全市场行情循环和盘中跟踪快照。'
          : '尚未生成今日竞价确认评估；系统会在竞价数据就绪后持续重试至11:30。'
      }
      return ''
    },
    rows () {
      let poolRows
      if (this.activePoolId === 'all') {
        poolRows = this.pools.reduce((all, pool) => all.concat(pool.rows || []), [])
      } else {
        poolRows = this.activePool ? (this.activePool.rows || []) : []
      }

      // "全部" is the pool-level view: one stock appears once per pool.
      // Keep phase tabs untouched so open and confirmation snapshots remain
      // available for side-by-side tracking.
      if (this.activeStage !== '全部') return poolRows
      if (this.activePoolId === 'stage_model') return poolRows
      if (this.activePoolId !== 'all') return this.dedupePoolRows(poolRows)

      const grouped = {}
      poolRows.forEach(row => {
        const key = row.pool_id || 'unknown'
        if (!grouped[key]) grouped[key] = []
        grouped[key].push(row)
      })
      return Object.keys(grouped).reduce(
        (all, poolId) => all.concat(this.dedupePoolRows(grouped[poolId])),
        []
      )
    },
    activePoolRowCount () {
      if (this.activePoolId === 'all') {
        return this.pools.reduce((total, pool) => total + this.poolDisplayCount(pool), 0)
      }
      return this.activePool ? this.poolDisplayCount(this.activePool) : 0
    },
    activePoolRecordCount () {
      return this.pools.reduce((total, pool) => total + Number(pool.total_count || 0), 0)
    },
    activePoolUniqueCount () {
      return new Set(this.rows.map(row => row.code).filter(Boolean)).size
    },
    activeViewRows () {
      return this.activeStage === '全部' ? this.rows : this.stageRows(this.activeStage)
    },
    activeViewRecordCount () {
      return this.activeViewRows.length
    },
    activeViewUniqueCount () {
      return new Set(this.activeViewRows.map(row => row.code).filter(Boolean)).size
    },
    filteredRows () {
      const query = this.searchText.toLowerCase()
      const filtered = [...this.rows]
        .filter(row => {
          const matchesStage = this.activeStage === '全部' || this.matchesStageTab(row, this.activeStage)
          const matchesSearch = !query || `${row.code} ${row.name} ${row.pool_label} ${row.sector}`.toLowerCase().includes(query)
          const matchesPositive = !this.positiveOnly || Number(row.change_pct) > 0
          return matchesStage && matchesSearch && matchesPositive
        })
      const visible = this.activeStage === '风险回避'
        ? this.dedupeRiskRows(filtered)
        : filtered
      return visible
        .sort((a, b) => {
          const direction = this.sortDirection === 'asc' ? 1 : -1
          if (this.viewMode === 'trade' && this.sortKey === 'score') {
            const gradeRank = { A: 4, B: 3, C: 2, X: 1 }
            const stateRank = { core_action: 3, conditional_watch: 2, risk_review: 1 }
            const gradeDiff = (Number(gradeRank[a.action_grade] || 0) - Number(gradeRank[b.action_grade] || 0)) * direction
            if (gradeDiff) return gradeDiff
            const stateDiff = (Number(stateRank[a.decision_state] || 0) - Number(stateRank[b.decision_state] || 0)) * direction
            if (stateDiff) return stateDiff
          }
          const aValue = Number(this.viewMode === 'trade' && this.sortKey === 'score' ? a.decision_score : a[this.sortKey])
          const bValue = Number(this.viewMode === 'trade' && this.sortKey === 'score' ? b.decision_score : b[this.sortKey])
          const aValid = Number.isFinite(aValue)
          const bValid = Number.isFinite(bValue)
          if (!aValid || !bValid) {
            if (aValid !== bValid) return aValid ? -1 : 1
          } else {
            const primaryDiff = (aValue - bValue) * direction
            if (primaryDiff) return primaryDiff
          }

          const changeDiff = Number(b.change_pct || 0) - Number(a.change_pct || 0)
          const scoreDiff = Number(b.score || 0) - Number(a.score || 0)
          return changeDiff || scoreDiff || String(a.code).localeCompare(String(b.code))
        })
    },
    filteredUniqueCount () {
      return new Set(this.filteredRows.map(row => row.code).filter(Boolean)).size
    },
    stageTabs () {
      if (this.viewMode !== 'research') return []
      if (this.activePoolId === 'all') {
        const values = []
        this.pools.forEach(pool => {
          // The stage-model count map also contains observation/action tiers.
          // Its explicit labels are the canonical tabs; do not leak derived
          // aliases such as "长周期观察" or "风险审计" into the all-pool view.
          const stages = pool.id === 'stage_model'
            ? STAGE_MODEL_FILTER_TABS
            : (pool.stage_labels || []).concat(Object.keys(pool.stage_counts || {}))
          stages.forEach(stage => {
            if (
              stage &&
              stage !== '未分组' &&
              !values.includes(stage)
            ) {
              values.push(stage)
            }
          })
        })
        return values
      }
      if (!this.activePool) return []
      if (this.activePool.id === 'stage_model') {
        return STAGE_MODEL_FILTER_TABS
      }
      return (this.activePool.stage_labels || []).filter(stage => stage)
    },
    stageTabGroups () {
      return STAGE_MODEL_TAB_GROUPS
    },
    activePoolLabel () {
      if (this.activePool) return this.activePool.label
      return this.viewMode === 'trade' ? '全部工作池' : '全部项目股池'
    },
    activePoolDescription () {
      if (this.viewMode === 'trade') {
        if (this.activePoolId === 'all') return '按决策优先级查看全部标的'
        return (this.activePool && this.activePool.description) || '统一决策工作池'
      }
      if (this.activePoolId === 'all') return '全部模型信号，按当前涨跌幅排列'
      if (this.activePoolId === 'stage_model') return '量价结构从建仓到出货的阶段切片'
      if (this.activePoolId === 'bullish_oversold') return '多头结构中的超跌修复与盘中承接确认'
      if (this.activePoolId === 'first_board_discovery') return '收盘研究宇宙、09:28竞价数据就绪确认、午盘新增与首板结果漏斗'
      return `${this.activePool ? this.activePool.label : '股池'} · 当前快照`
    },
    isStageModelSelected () {
      return Boolean(this.selectedRow && this.selectedRow.pool_id === 'stage_model')
    },
    currentStagePhase () {
      const cycles = this.stageTimeline && Array.isArray(this.stageTimeline.cycles)
        ? this.stageTimeline.cycles
        : []
      const stage = this.selectedRow && String(this.selectedRow.stage || '')
      const phases = cycles.reduce((all, cycle) => all.concat(cycle.phases || []), [])
      const aliases = {
        '建仓吸筹': ['建仓吸筹', '整理/吸筹'],
        '试盘突破': ['试盘突破', '整理/试盘'],
        '洗盘企稳': ['洗盘企稳', '洗盘后再拉升'],
        '拉升跟踪': ['拉升跟踪', '拉升']
      }
      const acceptedStages = aliases[stage] || [stage]
      const exact = [...phases].reverse().find(phase => acceptedStages.includes(phase.stage))
      return exact || [...phases].reverse()[0] || {}
    },
    isBullishOversoldSelected () {
      return Boolean(this.selectedRow && this.selectedRow.pool_id === 'bullish_oversold')
    },
    isFirstBoardSelected () {
      return Boolean(this.selectedRow && this.selectedRow.pool_id === 'first_board_discovery')
    },
    selectedL2Factor () {
      if (this.l2Watch.factor && Object.keys(this.l2Watch.factor).length) return this.l2Watch.factor
      return (this.selectedRow && this.selectedRow.l2_factor) || {}
    },
    hasSelectedL2Factor () {
      const factor = this.selectedL2Factor
      const status = String(factor.support_status || factor.stage_status || factor.status || '').toLowerCase()
      return status !== 'missing' && (
        Number(factor.coverage || 0) > 0 || Number(factor.event_count || 0) > 0
      )
    },
    selectedSupport () {
      const row = this.selectedRow || {}
      const factor = this.selectedL2Factor || {}
      const windowData = factor.support_window && typeof factor.support_window === 'object'
        ? factor.support_window
        : {}
      const score = Number(factor.support_score !== undefined ? factor.support_score : (factor.score !== undefined ? factor.score : row.support_score || 0))
      const rollingScore = Number(windowData.support_decay_score !== undefined
        ? windowData.support_decay_score
        : (row.support_rolling_score || 0))
      const coverage = Number(factor.coverage !== undefined ? factor.coverage : (row.support_coverage || 0))
      const bookImbalance = factor.book_imbalance !== undefined ? factor.book_imbalance : row.support_book_imbalance
      const activeBuyRatio = factor.active_buy_ratio !== undefined ? factor.active_buy_ratio : row.support_active_buy_ratio
      const status = String(factor.stage_status || row.l2_stage_status || factor.status || row.l2_status || 'missing')
      const divergent = score >= 12 && bookImbalance !== null && bookImbalance !== undefined && Number(bookImbalance) <= -0.2
      const live = this.l2Watch.state === 'live'
      const fresh = live || Boolean(row.support_fresh)
      let confidence = row.support_confidence_text || '等待数据'
      let signal = row.support_signal_text || row.support_text || '承接待补'
      let tone = row.support_confidence || 'missing'
      if (status === 'conflict') {
        confidence = '承接冲突'
        signal = '承接冲突'
        tone = 'conflict'
      } else if (!fresh) {
        confidence = row.support_updated_at ? '数据过期' : '等待数据'
        signal = row.support_updated_at ? '承接过期' : '承接待补'
        tone = row.support_updated_at ? 'stale' : 'missing'
      } else if (coverage < 2) {
        confidence = '低覆盖'
        signal = '承接低覆盖'
        tone = 'low'
      } else if (status === 'neutral') {
        confidence = '中性观察'
        signal = '承接中性'
        tone = 'medium'
      } else if (divergent) {
        confidence = '盘口分歧'
        signal = '承接偏强 · 盘口分歧'
        tone = 'medium'
      } else if (status === 'confirm') {
        confidence = '高置信'
        signal = `承接确认 ${Math.round(rollingScore || score)}`
        tone = 'high'
      }
      return {
        signal,
        confidence,
        tone,
        score,
        rollingScore,
        coverage,
        bookImbalance,
        activeBuyRatio,
        freshness: live ? '实时' : (fresh ? '今日有效' : '非实时'),
        updatedAt: String(factor.updated_at || windowData.updated_at || windowData.last_at || row.support_updated_at || '')
      }
    },
    selectedRealtimePrice () {
      return Number((this.l2Watch.quote || {}).price || (this.selectedRow && this.selectedRow.price) || 0)
    },
    selectedRealtimeChange () {
      return this.liveChangePct(this.l2Watch.quote, this.selectedRow)
    },
    l2StateText () {
      const labels = {
        idle: '未连接',
        connecting: '连接中',
        live: '实时盯盘',
        degraded: '部分流异常',
        offline: '链路不可用'
      }
      return labels[this.l2Watch.state] || this.l2Watch.state
    },
    l2StreamKinds () {
      return [
        'quote', 'depth', 'level2_depth', 'transaction', 'transaction_detail', 'enhanced_transactions',
        'order_detail', 'buy_queue', 'sell_queue', 'buy_cancel', 'sell_cancel', 'bid_ask_rank',
        'call_auction', 'capital_flow', 'big_order_flow'
      ]
    },
    selectedPlatformLevels () {
      const fallback = {
        available: false,
        defense_price: 0,
        attack_price: 0,
        defense_rule: '',
        attack_rule: '',
        state: '待计算',
        source: '',
        structure_label: '',
        box_kind: '',
        start_date: '',
        end_date: '',
        distance_to_defense_pct: 0,
        distance_to_attack_pct: 0
      }
      const source = (this.selectedRow && this.selectedRow.platform_levels) || fallback
      const levels = { ...fallback, ...source }
      const price = Number(this.selectedRealtimePrice || 0)
      const defense = Number(levels.defense_price || 0)
      const attack = Number(levels.attack_price || 0)
      if (!levels.available || price <= 0 || defense <= 0 || attack <= defense) return levels
      levels.distance_to_defense_pct = (price / defense - 1) * 100
      levels.distance_to_attack_pct = (price / attack - 1) * 100
      levels.position_pct = (price - defense) * 100 / (attack - defense)
      return this.platformLevelsAtPrice(levels, price)
    },
    selectedPlatformMarkerStyle () {
      const position = Number(this.selectedPlatformLevels.position_pct || 0)
      const clamped = Math.max(2, Math.min(98, Number.isFinite(position) ? position : 0))
      return { left: `${clamped}%` }
    },
    selectedDailyPositions () {
      return (this.selectedRow && this.selectedRow.daily_positions) || {
        available: false,
        trade_date: '',
        ma5: 0,
        ma10: 0,
        ma13: 0,
        ma17: 0,
        ma55: 0,
        intraday_high: 0,
        intraday_low: 0,
        intraday_source: 'unavailable'
      }
    },
    dailyMaRows () {
      return [
        { key: 'ma5', label: '5日线' },
        { key: 'ma10', label: '10日线' },
        { key: 'ma13', label: '13日线' },
        { key: 'ma17', label: '17日线' },
        { key: 'ma55', label: '55日线' }
      ]
    },
    selectedMaCrossSignals () {
      return this.activeMaCrossSignals(this.selectedRow)
    },
    uniqueTrackedCount () {
      return new Set(this.rows.map(row => row.code).filter(Boolean)).size
    },
    visiblePoolCount () {
      return this.uniqueTrackedCount
    },
    sortLabel () {
      const labels = {
        price: '现价',
        change_pct: '涨跌幅',
        score: '评分'
      }
      const direction = this.sortDirection === 'asc' ? '升序' : '降序'
      const label = this.viewMode === 'trade' && this.sortKey === 'score'
        ? '决策优先级'
        : (labels[this.sortKey] || '涨跌幅')
      return `${label}${direction}`
    },
    positiveCount () {
      return this.rows.filter(row => Number(row.change_pct) > 0).length
    },
    positiveRatio () {
      return this.rows.length ? Math.round(this.positiveCount * 1000 / this.rows.length) / 10 : 0
    },
    liveQuoteCount () {
      return this.rows.filter(row => row.quote_status === 'live').length
    },
    liveCoverage () {
      return this.rows.length ? Math.round(this.liveQuoteCount * 1000 / this.rows.length) / 10 : 0
    },
    isMarketOpen () {
      return Boolean(this.dashboard.market_status && this.dashboard.market_status.is_open)
    },
    marketStatus () {
      return (this.dashboard.market_status && this.dashboard.market_status.label) || '等待行情'
    },
    marketDate () {
      const value = (this.dashboard.market_status && this.dashboard.market_status.server_time) || this.dashboard.updated_at
      return value ? value.slice(0, 10).replace(/-/g, '.') : '--.--.--'
    },
    quoteStatusText () {
      const status = this.dashboard.source_status && this.dashboard.source_status.quotes
      return status === 'live' ? '实时行情已接入' : status === 'degraded' ? '行情降级，使用快照' : '等待行情接口'
    },
    quoteUpdatedText () {
      const value = this.dashboard.source_status && this.dashboard.source_status.quote_updated_at
      return value ? `最近刷新 ${value.slice(11, 19)}` : '尚未获得实时刷新'
    },
    fqgateStatusText () {
      if (this.fqgateOverview.status === 'ok' && this.fqgateAlertState === 'connected') return '行情排行与短线异动在线'
      if (this.fqgateOverview.status === 'ok') return '排行在线 · 异动流等待连接'
      if (this.fqgateOverview.status === 'degraded') return '实时排行异常 · 沿用最近有效快照'
      if (this.fqgateStatus.status === 'degraded') return 'FQGate会话降级'
      if (this.fqgateStatus.status === 'missing') return 'FQGate未配置'
      return this.fqgateOverview.status === 'error' ? '市场雷达暂不可用' : '等待FQGate市场数据'
    },
    fqgateOverviewRows () {
      const coverage = this.fqgateOverview.coverage || {}
      return [
        { key: 'market', label: '市场排行', value: coverage.market_ranking || '--' },
        { key: 'security', label: '个股排行', value: coverage.security_ranking || '--' },
        { key: 'limit', label: '涨停统计', value: coverage.limit_up_statistics || '--' }
      ]
    },
    lastUpdatedText () {
      return this.dashboard.updated_at ? this.dashboard.updated_at.slice(11, 19) : '--:--:--'
    },
    snapshotUpdatedText () {
      const values = this.pools.map(pool => pool.snapshot_at).filter(Boolean).sort()
      return values.length ? values[values.length - 1] : '暂无'
    },
    pulseRows () {
      const groups = [
        { label: '上涨标的', count: this.rows.filter(row => Number(row.change_pct) > 0).length, tone: 'bar-mint' },
        { label: '实时行情', count: this.liveQuoteCount, tone: 'bar-blue' },
        { label: '阶段确认', count: this.rows.filter(row => row.status === '已确认' || row.status === '承接确认').length, tone: 'bar-yellow' },
        { label: '风险提示', count: this.rows.filter(row => row.stage === '出货风险' || (row.risks || []).length).length, tone: 'bar-coral' }
      ]
      const max = Math.max(...groups.map(item => item.count), 1)
      return groups.map(item => ({ ...item, percent: Math.round(item.count * 100 / max) }))
    }
  },
  watch: {
    viewMode () {
      this.activePoolId = 'all'
      this.activeStage = '全部'
      this.selectedRow = null
      this.detailPanelOpen = false
      this.stopL2Watch()
    },
    activePoolId () {
      this.activeStage = '全部'
      this.selectedRow = null
      this.detailPanelOpen = false
      this.stopL2Watch()
    }
  },
  mounted () {
    this.fetchDashboard()
    this.fetchFQGateStatus()
    this.restartTimer()
    window.addEventListener('keydown', this.handleGlobalKeydown)
  },
  beforeDestroy () {
    this.stopTimer()
    this.stopL2Watch()
    this.stopFQGateAlerts()
    window.removeEventListener('keydown', this.handleGlobalKeydown)
  },
  methods: {
    selectViewMode (mode) {
      if (!['trade', 'research'].includes(mode)) return
      this.viewMode = mode
      this.sortKey = mode === 'trade' ? 'score' : 'change_pct'
      this.sortDirection = 'desc'
    },
    async fetchDashboard (forceRefresh = false) {
      this.loading = true
      this.error = ''
      try {
        const query = forceRefresh ? '?refresh=true' : ''
        const response = await fetch(`${API_BASE}/api/v1/pools/dashboard${query}`, {
          headers: { Accept: 'application/json' }
        })
        if (!response.ok) throw new Error(`接口返回 ${response.status}`)
        const payload = await response.json()
        this.dashboard = payload
        this.fetchFQGateSnapshot(payload)
        this.fetchFQGateOverview(payload)
        if (
          this.selectedOpportunityBoard &&
          !this.opportunityBoards.some(board => board.board_name === this.selectedOpportunityBoard)
        ) {
          this.selectedOpportunityBoard = ''
        }
        this.loaded = true
        if (this.selectedRow) {
          const refreshed = this.rows.find(row => row.code === this.selectedRow.code)
          this.selectedRow = refreshed || null
          if (!refreshed) {
            this.detailPanelOpen = false
            this.stopL2Watch()
          }
        }
      } catch (error) {
        this.error = error.message || '无法连接后端股池接口'
      } finally {
        this.loading = false
      }
    },
    restartTimer () {
      this.stopTimer()
      if (this.refreshSeconds > 0) {
        this.refreshTimer = window.setInterval(() => this.fetchDashboard(true), this.refreshSeconds * 1000)
      }
    },
    stopTimer () {
      if (this.refreshTimer) {
        window.clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    async fetchFQGateSnapshot (dashboardPayload) {
      const requestId = ++this.fqgateRequestId
      const pools = Array.isArray(dashboardPayload.pools) ? dashboardPayload.pools : []
      const codes = [...new Set(pools.reduce((values, pool) => values.concat((pool.rows || []).map(row => row.code)), []).filter(Boolean))]
      if (!codes.length) return
      const controller = new AbortController()
      const timeout = window.setTimeout(() => controller.abort(), 10000)
      try {
        const response = await fetch(`${API_BASE}/api/v1/fqgate/market-snapshot`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ codes }),
          signal: controller.signal
        })
        if (!response.ok) throw new Error(`接口返回 ${response.status}`)
        const snapshot = await response.json()
        if (requestId !== this.fqgateRequestId) return
        this.fqgateMarket = snapshot
        this.applyFQGateQuotes(snapshot.quotes || {})
        if (
          this.selectedOpportunityBoard &&
          !this.opportunityBoards.some(board => board.board_name === this.selectedOpportunityBoard)
        ) this.selectedOpportunityBoard = ''
      } catch (error) {
        if (requestId !== this.fqgateRequestId) return
        this.fqgateMarket = {
          ...this.fqgateMarket,
          status: 'error',
          error: error.name === 'AbortError' ? '请求超时' : (error.message || 'FQGate实时聚合失败')
        }
      } finally {
        window.clearTimeout(timeout)
      }
    },
    async fetchFQGateStatus () {
      try {
        const response = await fetch(`${API_BASE}/api/v1/fqgate/status`, { headers: { Accept: 'application/json' } })
        if (!response.ok) throw new Error(`接口返回 ${response.status}`)
        this.fqgateStatus = await response.json()
        if (this.fqgateStatus.status === 'ok' || this.fqgateStatus.status === 'degraded') this.startFQGateAlerts()
      } catch (error) {
        this.fqgateStatus = { ...this.fqgateStatus, status: 'error', error: error.message || 'FQGate状态读取失败' }
      }
    },
    async fetchFQGateOverview (dashboardPayload) {
      const pools = Array.isArray(dashboardPayload.pools) ? dashboardPayload.pools : []
      const codes = [...new Set(pools.reduce((values, pool) => values.concat((pool.rows || []).map(row => row.code)), []).filter(Boolean))]
      if (!codes.length) return
      try {
        const response = await fetch(`${API_BASE}/api/v1/fqgate/market-overview`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ codes })
        })
        if (!response.ok) throw new Error(`接口返回 ${response.status}`)
        this.fqgateOverview = await response.json()
      } catch (error) {
        this.fqgateOverview = { ...this.fqgateOverview, status: 'error', errors: { dashboard: error.message || '市场概览失败' } }
      }
    },
    startFQGateAlerts () {
      this.stopFQGateAlerts()
      if (typeof window.WebSocket === 'undefined') return
      this.fqgateAlertState = 'connecting'
      const endpoint = new URL(`${API_BASE}/api/v1/fqgate/alerts`, window.location.origin)
      endpoint.protocol = endpoint.protocol === 'https:' ? 'wss:' : 'ws:'
      endpoint.searchParams.set('market', 'USZA')
      const socket = new window.WebSocket(endpoint.toString())
      this.fqgateAlertSocket = socket
      socket.onmessage = event => {
        if (this.fqgateAlertSocket !== socket) return
        let message
        try { message = JSON.parse(event.data) } catch (error) { return }
        if (message.type === 'status') this.fqgateAlertState = 'connected'
        if (message.type === 'alert') {
          this.fqgateAlertState = 'connected'
          this.fqgateAlerts = [{ ...message, received_at: new Date().toISOString() }, ...this.fqgateAlerts].slice(0, 12)
        }
        if (message.type === 'error') this.fqgateAlertState = 'degraded'
      }
      socket.onerror = () => { if (this.fqgateAlertSocket === socket) this.fqgateAlertState = 'degraded' }
      socket.onclose = () => {
        if (this.fqgateAlertSocket !== socket) return
        this.fqgateAlertSocket = null
        this.fqgateAlertState = 'offline'
        this.fqgateAlertReconnectTimer = window.setTimeout(() => this.startFQGateAlerts(), 15000)
      }
    },
    stopFQGateAlerts () {
      if (this.fqgateAlertReconnectTimer) {
        window.clearTimeout(this.fqgateAlertReconnectTimer)
        this.fqgateAlertReconnectTimer = null
      }
      const socket = this.fqgateAlertSocket
      this.fqgateAlertSocket = null
      if (socket) socket.close()
      this.fqgateAlertState = 'idle'
    },
    alertText (event) {
      if (!event || typeof event !== 'object') return String(event || '短线异动')
      for (const key of ['message', 'name', 'reason', 'title', 'security_name', 'code']) {
        if (event[key] !== undefined && event[key] !== '') return String(event[key])
      }
      try {
        const text = JSON.stringify(event)
        return text.length > 120 ? `${text.slice(0, 117)}...` : text
      } catch (error) { return '短线异动' }
    },
    applyFQGateQuotes (quotes) {
      if (!quotes || typeof quotes !== 'object') return
      const applyQuotesToPools = pools => (pools || []).map(pool => ({
        ...pool,
        rows: (pool.rows || []).map(row => {
          const quote = quotes[row.code]
          const changePct = this.liveChangePct(quote, row)
          return quote
            ? {
                ...row,
                price: this.validLivePrice(quote.price) ? quote.price : row.price,
                // A malformed FQGate prior-close field must not overwrite a
                // valid daily snapshot and turn the UI into +236250%.
                change_pct: changePct,
                pre_close: this.validLivePreClose(quote, row.code) ? quote.pre_close : row.pre_close,
                open: quote.open,
                high: quote.high,
                low: quote.low,
                quote_updated_at: quote.updated_at,
                quote_status: 'live',
                quote_source: 'fqgate'
              }
            : row
        })
      }))
      const pools = applyQuotesToPools(this.rawPools)
      const workPools = applyQuotesToPools(this.workPools)
      this.dashboard = {
        ...this.dashboard,
        pools,
        work_pools: workPools,
        source_status: {
          ...(this.dashboard.source_status || {}),
          fqgate_quotes: Object.keys(quotes).length ? 'ok' : 'missing',
          fqgate_quote_count: Object.keys(quotes).length,
          fqgate_quote_updated_at: this.fqgateMarket.updated_at || ''
        }
      }
      if (this.selectedRow) {
        const refreshed = this.pools
          .find(pool => pool.id === (this.selectedRow.work_pool_id || this.selectedRow.pool_id))
        const row = refreshed && (refreshed.rows || []).find(item => item.code === this.selectedRow.code)
        if (row) this.selectedRow = row
      }
    },
    selectPool (poolId) {
      this.activePoolId = poolId
    },
    selectRow (row) {
      this.selectedRow = row
      this.detailPanelOpen = Boolean(row)
      this.stageTimeline = { cycles: [] }
      if (row && row.pool_id === 'stage_model') this.fetchStageTimeline(row.code)
      this.startL2Watch(row)
    },
    closeDetailPanel () {
      this.detailPanelOpen = false
      this.stopL2Watch()
    },
    handleGlobalKeydown (event) {
      if (event.key === 'Escape') this.closeDetailPanel()
    },
    startL2Watch (row) {
      this.stopL2Watch()
      if (!row || !row.code || typeof window.WebSocket === 'undefined') return
      this.l2Watch = {
        state: 'connecting',
        factor: row.l2_factor || {},
        quote: {},
        streams: {},
        events: [],
        error: ''
      }
      const endpoint = new URL(`${API_BASE}/api/v1/fqgate/watch/${encodeURIComponent(row.code)}`, window.location.origin)
      endpoint.protocol = endpoint.protocol === 'https:' ? 'wss:' : 'ws:'
      endpoint.searchParams.set('stage', row.stage || '')
      const socket = new window.WebSocket(endpoint.toString())
      this.l2Socket = socket
      socket.onmessage = event => {
        if (this.l2Socket !== socket) return
        let message
        try {
          message = JSON.parse(event.data)
        } catch (error) {
          return
        }
        if (message.type === 'stream_status') {
          this.$set(this.l2Watch.streams, message.kind, message.state)
          this.l2Watch.state = 'live'
        } else if (message.type === 'factor') {
          this.l2Watch.factor = message.factor || {}
          this.l2Watch.state = 'live'
        } else if (message.type === 'quote') {
          this.l2Watch.quote = message.quote || {}
          this.l2Watch.state = 'live'
        } else if (message.type === 'event') {
          this.l2Watch.events = [{ kind: message.kind, event: message.event }, ...this.l2Watch.events].slice(0, 8)
        } else if (message.type === 'error') {
          if (message.kind) this.$set(this.l2Watch.streams, message.kind, 'error')
          this.l2Watch.error = message.message || 'L2流异常'
          this.l2Watch.state = Object.values(this.l2Watch.streams).includes('connected') ? 'degraded' : 'offline'
        }
      }
      socket.onerror = () => {
        if (this.l2Socket === socket) this.l2Watch.error = 'FQGate L2连接失败'
      }
      socket.onclose = () => {
        if (this.l2Socket !== socket) return
        this.l2Socket = null
        if (!this.detailPanelOpen || !this.selectedRow || this.selectedRow.code !== row.code) return
        this.l2Watch.state = 'offline'
        this.l2ReconnectTimer = window.setTimeout(() => this.startL2Watch(this.selectedRow), 5000)
      }
    },
    stopL2Watch () {
      if (this.l2ReconnectTimer) {
        window.clearTimeout(this.l2ReconnectTimer)
        this.l2ReconnectTimer = null
      }
      const socket = this.l2Socket
      this.l2Socket = null
      if (socket) socket.close()
      this.l2Watch = { state: 'idle', factor: {}, quote: {}, streams: {}, events: [], error: '' }
    },
    l2StatusClass (status) {
      if (status === 'strong_support') return 'l2-strong-support'
      if (status === 'support') return 'l2-support'
      if (status === 'weak_support') return 'l2-weak-support'
      if (status === 'pressure') return 'l2-pressure'
      if (status === 'confirm') return 'l2-confirm'
      if (status === 'conflict') return 'l2-conflict'
      if (status === 'neutral') return 'l2-neutral'
      return 'l2-missing'
    },
    l2KindLabel (kind) {
      const labels = {
        quote: '实时行情',
        depth: '五档盘口',
        level2_depth: '十档盘口',
        transaction: '逐笔成交',
        transaction_detail: '逐笔明细',
        enhanced_transactions: '增强逐笔',
        order_detail: '委托明细',
        buy_queue: '买方队列',
        sell_queue: '卖方队列',
        buy_cancel: '买方撤单',
        sell_cancel: '卖方撤单',
        bid_ask_rank: '买卖排名',
        call_auction: '集合竞价',
        capital_flow: '资金流',
        big_order_flow: '大单流'
      }
      return labels[kind] || kind
    },
    l2EventCount (kind) {
      const counts = this.selectedL2Factor.kind_counts || {}
      return Number(counts[kind] || 0)
    },
    ratioText (value, signed) {
      if (value === null || value === undefined || value === '') return '--'
      const number = Number(value)
      if (!Number.isFinite(number)) return '--'
      return `${signed && number > 0 ? '+' : ''}${(number * 100).toFixed(1)}%`
    },
    selectOpportunity (stock) {
      const pool = this.pools.find(item => item.id === stock.pool_id) || this.pools.find(item => (item.rows || []).some(row => row.code === stock.code))
      const row = pool && (pool.rows || []).find(item => item.code === stock.code)
      if (!row) return
      this.activePoolId = pool.id
      this.activeStage = '全部'
      this.$nextTick(() => this.selectRow(row))
    },
    selectOpportunityBoard (boardName) {
      const value = String(boardName || '')
      this.selectedOpportunityBoard = this.selectedOpportunityBoard === value ? '' : value
    },
    opportunityStateClass (state) {
      if (state === '强共振机会' || state === 'L2确认') return 'opportunity-strong'
      if (state === '重点观察') return 'opportunity-focus'
      if (state === '机会失效' || state === 'L2冲突') return 'opportunity-invalid'
      return 'opportunity-wait'
    },
    async fetchStageTimeline (code) {
      const requestId = ++this.stageTimelineRequestId
      this.stageTimelineLoading = true
      this.stageTimeline = { cycles: [] }
      try {
        const response = await fetch(`${API_BASE}/api/v1/pools/stage-timeline/${encodeURIComponent(code)}`, {
          headers: { Accept: 'application/json' }
        })
        if (!response.ok) throw new Error(`接口返回 ${response.status}`)
        const payload = await response.json()
        if (requestId === this.stageTimelineRequestId && this.selectedRow && this.selectedRow.code === code) {
          this.stageTimeline = payload
        }
      } catch (error) {
        if (requestId === this.stageTimelineRequestId) this.stageTimeline = { cycles: [], error: error.message || '阶段时间轴读取失败' }
      } finally {
        if (requestId === this.stageTimelineRequestId) this.stageTimelineLoading = false
      }
    },
    activeMaCrossSignals (row) {
      const signals = row && row.ma_cross_signals
      if (!signals || typeof signals !== 'object') return []
      return Object.values(signals).filter(signal => signal && signal.active)
    },
    maCrossClass (signal) {
      if (signal && signal.intraday_triggered) return 'ma-cross-intraday'
      if (signal && signal.confirmed) return 'ma-cross-confirmed'
      return 'ma-cross-active'
    },
    channelDirectionTitle (row) {
      const detail = row && row.channel_direction_detail
      if (!detail || typeof detail !== 'object') return '通道方向数据'
      const slope = Number(detail.slope_pct)
      const slopeText = Number.isFinite(slope) ? `${slope > 0 ? '+' : ''}${slope.toFixed(2)}%` : '--'
      return `通道中轨5日变化 ${slopeText} · ${detail.compare_trade_date || '--'} → ${detail.trade_date || '--'}`
    },
    poolDisplayCount (pool) {
      if (pool.id === 'stage_model') return pool.total_count || 0
      return new Set((pool.rows || []).map(row => row.code).filter(Boolean)).size
    },
    dedupePoolRows (rows) {
      const selected = new Map()
      rows.forEach(row => {
        const previous = selected.get(row.code)
        if (!previous || this.rowStagePriority(row) > this.rowStagePriority(previous)) {
          selected.set(row.code, row)
        }
      })
      return Array.from(selected.values())
    },
    dedupeRiskRows (rows) {
      const selected = new Map()
      rows.forEach(row => {
        const previous = selected.get(row.code)
        if (!previous || this.riskRowPriority(row) > this.riskRowPriority(previous)) {
          selected.set(row.code, row)
        }
      })
      return Array.from(selected.values())
    },
    riskRowPriority (row) {
      if (row.stage === '出货风险' || /出货/.test(String(row.risk_level || ''))) return 4
      if (this.actionReference(row) === '风险回避') return 3
      if (/跌破|破位|失效/.test(String(row.intraday_status || ''))) return 2
      return 1
    },
    rowStagePriority (row) {
      const stage = String(row.stage || '')
      if (/已封首板|炸板|未封/.test(stage)) return 4
      if (/承接|复筛|确认/.test(stage)) return 3
      if (/初筛|开盘|竞价/.test(stage)) return 2
      return 1
    },
    sortBy (key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
        return
      }
      this.sortKey = key
      this.sortDirection = 'desc'
    },
    sortIndicator (key) {
      if (this.sortKey !== key) return '↕'
      return this.sortDirection === 'asc' ? '↑' : '↓'
    },
    stageCount (stage) {
      return new Set(this.stageRows(stage).map(row => row.code).filter(Boolean)).size
    },
    stageRows (stage) {
      if (!stage) return []
      const pools = this.activePoolId === 'all' ? this.pools : (this.activePool ? [this.activePool] : [])
      return pools.reduce((all, pool) => {
        const matched = (pool.rows || []).filter(row => this.matchesStageTab(row, stage))
        return all.concat(matched)
      }, [])
    },
    stageTabCountLabel (stage) {
      const rows = this.stageRows(stage)
      const uniqueCount = new Set(rows.map(row => row.code).filter(Boolean)).size
      return rows.length > uniqueCount ? `${uniqueCount}只/${rows.length}条` : `${uniqueCount}只`
    },
    stageTabCountTitle (stage) {
      const rows = this.stageRows(stage)
      const uniqueCount = new Set(rows.map(row => row.code).filter(Boolean)).size
      return `${uniqueCount}只股票，${rows.length}条池内信号记录；标签之间可能重叠`
    },
    matchesStageTab (row, stage) {
      if (!row || !stage) return false
      const stageAliases = {
        '当前行动池': '短周期跟踪',
        '优先关注层': '优先关注',
        '通道未滚': '未出通道',
        '筹码近峰': '靠近主成本区',
        '筹码趋势T5': 'T+5观察条件'
      }
      stage = stageAliases[stage] || stage
      if (row.pool_id === 'bullish_oversold') {
        if (['A级', 'B级', 'C级'].includes(stage)) return row.signal_tier === stage
        if (['今日新增', '跟踪中', '承接确认', '已失效', '已完成'].includes(stage)) {
          return row.stage === stage || row.tracking_status === stage
        }
      }
      if (row.pool_id === 'first_board_discovery' && ['A级', 'B级', 'C级'].includes(stage)) {
        return row.signal_tier === stage
      }
      if (!STAGE_MODEL_FILTER_TABS.includes(stage)) return row.stage === stage
      if (row.pool_id !== 'stage_model') return false
      if (stage === '短周期跟踪') return row.observation_tier === '当前行动池'
      if (stage === '长周期观察池') return row.observation_tier === '长周期观察池'
      if (stage === '未出通道') return row.tdx_channel_not_gun === true
      if (stage === '靠近主成本区') return row.near_chip_peak === true
      if (stage === '筹码持续收敛') return row.chip_converging === true
      if (stage === '筹码重新发散') return row.chip_expanding === true
      if (stage === '收敛但价格偏离') return row.chip_converging_price_deviated === true
      if (stage === '筹码峰下移') return row.chip_peak_falling === true
      if (stage === 'T+5观察条件') return row.chip_trend_t5 === true
      if (stage === '优先关注') return ['确认关注', '早期洗盘'].includes(this.actionReference(row))
      if (stage === '风险回避') return this.isRiskAvoidance(row)
      if (STAGE_MODEL_PHASE_TABS.includes(stage)) return row.stage === stage
      return this.actionReference(row) === stage
    },
    poolMark (id) {
      return {
        core_action: 'A',
        conditional_watch: 'B',
        risk_review: '!',
        auction_limit_potential: '↗',
        auction_dragon: '◆',
        trend_start: '≈',
        oversold_reversal: '⌁',
        stage_model: '◒',
        bullish_oversold: '◓',
        first_board_discovery: '◇'
      }[id] || '•'
    },
    poolMarkClass (id) {
      return `mark-${id}`
    },
    roleClass (role) {
      if (['market_leader', 'board_leader'].includes(role)) return 'role-core'
      if (role === 'front_runner') return 'role-front'
      if (['middle_follow', 'rear'].includes(role)) return 'role-follow'
      return 'role-pending'
    },
    decisionStatusClass (row) {
      if (row.decision_state === 'core_action') return 'status-confirmed'
      if (row.decision_state === 'risk_review') return 'status-risk'
      if (row.decision_state === 'conditional_watch') return 'status-watch'
      return this.statusClass(row)
    },
    statusClass (row) {
      if (row.intraday_status && /跌破|风险/.test(row.intraday_status)) return 'status-risk'
      if (row.intraday_status && /突破/.test(row.intraday_status)) return 'status-positive'
      if (row.stage === '出货风险' || ((row.risks || []).length > 0 && row.change_pct < 0)) return 'status-risk'
      if (row.status === '已确认' || row.status === '承接确认') return 'status-confirmed'
      if (row.change_pct > 0) return 'status-positive'
      return 'status-watch'
    },
    actionTierClass (tier) {
      return {
        '确认关注': 'tier-confirmed-attention',
        '早期洗盘': 'tier-early-wash',
        '等待确认': 'tier-wait',
        '结构观察': 'tier-structure',
        '防追高': 'tier-no-chase',
        '长周期观察': 'tier-long',
        '风险回避': 'tier-risk'
      }[tier] || 'tier-neutral'
    },
    actionReference (row) {
      if (!row) return ''
      return row.action_reference || row.action_tier || ''
    },
    isRiskAvoidance (row) {
      if (!row) return false
      if (this.actionReference(row) === '风险回避') return true
      if (['出货风险', '出货观察', '出货结束', '风险审计'].includes(row.stage)) return true
      const riskLevel = String(row.risk_level || '')
      // Keep unconfirmed one-day audits visible in details without treating
      // an otherwise positive tracking row as an actionable avoid signal.
      if (riskLevel && !['无明显风险', '无风险', '低风险', '正常', '风险审计'].includes(riskLevel)) {
        if (/出货|破位|卖压|高位/.test(riskLevel)) return true
      }
      return /风险回避|出货|跌破|破位|失效|卖压/.test(`${row.tracking_status || ''} ${row.intraday_status || ''}`)
    },
    statusText (row) {
      if (row.intraday_status) return row.intraday_status
      if (row.tracking_status && row.tracking_status !== '当前阶段确认') return row.tracking_status
      if (row.stage === '出货风险') return '风险'
      if (row.status === '已确认' || row.status === '承接确认') return '已确认'
      if (row.status) return row.status
      return row.change_pct > 0 ? '走强' : '观察'
    },
    stageTrackingText (row) {
      if (!row || !['stage_model', 'bullish_oversold'].includes(row.pool_id)) return ''
      if (row.intraday_status) return row.intraday_status
      if (row.tracking_status && row.tracking_status !== '当前阶段确认') return row.tracking_status
      return ''
    },
    stageTrackingClass (row) {
      const text = this.stageTrackingText(row)
      if (/跌破|风险|失效/.test(text)) return 'stage-state-risk'
      if (/突破/.test(text)) return 'stage-state-breakout'
      if (/延续|观察/.test(text)) return 'stage-state-watch'
      return 'stage-state-neutral'
    },
    primaryReason (row) {
      return row.reasons && row.reasons.length ? row.reasons[0] : (row.quote_status === 'live' ? '实时行情已刷新' : '等待更多信号')
    },
    supportToneClass (row) {
      const tone = String((row && (row.tone || row.support_confidence)) || '')
      if (tone === 'high') return 'support-positive'
      if (tone === 'conflict') return 'support-negative'
      if (tone === 'medium' || tone === 'low') return 'support-caution'
      return 'support-muted'
    },
    platformToneClass (levels) {
      const state = String((levels && (levels.breakout_state || levels.state)) || '')
      if (/failed|跌破|失效/.test(state)) return 'box-failed'
      if (/confirmed|量能确认|有效突破/.test(state)) return 'box-confirmed'
      if (/testing|试突破|retest|回踩|回到箱内|near|临近|extended|延伸/.test(state)) return 'box-testing'
      return 'box-inside'
    },
    platformTitle (levels) {
      const kind = String((levels && levels.box_kind) || '')
      if (kind === 'n_breakout') return 'N字结构'
      if (kind === 'dynamic_range') return '区间位置'
      return '模型平台'
    },
    platformDefenseLabel (levels) {
      const kind = String((levels && levels.box_kind) || '')
      if (kind === 'n_breakout') return '回踩L2'
      if (kind === 'dynamic_range') return '区间底'
      return '防守位'
    },
    platformAttackLabel (levels) {
      const kind = String((levels && levels.box_kind) || '')
      if (kind === 'n_breakout') return 'N顶H1'
      if (kind === 'dynamic_range') return '区间顶'
      return '突破位'
    },
    platformLevelsAtPrice (source, price) {
      const levels = { ...source }
      const defense = Number(levels.defense_price || 0)
      const attack = Number(levels.attack_price || 0)
      const kind = String(levels.box_kind || 'model')
      if (kind === 'dynamic_range') {
        levels.breakout_state = 'reference'
        levels.state = price >= attack * 0.97 ? '临近区间上沿 · 等待N字结构' : '动态区间内 · 等待N字结构'
      } else if (price < defense) {
        levels.breakout_state = 'failed'
        levels.state = kind === 'n_breakout' ? '跌破回踩防守位 · 结构失效' : '盘中低于防守位（待收盘确认）'
      } else if (kind === 'n_breakout' && levels.breakout_confirmed && price >= attack * 1.08) {
        levels.breakout_state = 'extended'
        levels.state = '突破延伸 · 高于N顶8%以上不追高'
      } else if (levels.breakout_confirmed && price >= attack) {
        levels.breakout_state = 'confirmed'
        levels.state = kind === 'n_breakout' ? 'N字有效突破 · 收盘量能确认' : '有效突破 · 收盘确认'
      } else if (price >= attack) {
        levels.breakout_state = 'testing'
        levels.state = kind === 'n_breakout' ? '盘中试突破N顶 · 待收盘/量能确认' : '盘中站上进攻位（待收盘/量能确认）'
      } else if (levels.historical_breakout || levels.close_broke_box) {
        levels.breakout_state = 'retest'
        levels.state = kind === 'n_breakout' ? '突破后回踩N顶 · 观察承接' : '突破后回到平台 · 观察承接'
      } else if (price >= attack * 0.97) {
        levels.breakout_state = 'near'
        levels.state = kind === 'n_breakout' ? '临近N顶 · 等待有效突破' : '临近突破位 · 等待确认'
      } else {
        levels.breakout_state = kind === 'n_breakout' ? 'forming' : 'inside'
        levels.state = kind === 'n_breakout' ? 'N字右侧构建 · 等待接近N顶' : '平台区间内'
      }
      return levels
    },
    platformDistanceText (levels) {
      const distance = Number((levels && levels.distance_to_attack_pct) || 0)
      const kind = String((levels && levels.box_kind) || '')
      const target = kind === 'n_breakout' ? 'N顶' : (kind === 'dynamic_range' ? '区间顶' : '突破位')
      if (!Number.isFinite(distance)) return `${target}距离待补`
      return distance >= 0
        ? `高于${target} ${this.signedPercent(distance)}`
        : `距${target} ${Math.abs(distance).toFixed(2)}%`
    },
    platformPositionText (levels) {
      const position = Number((levels && levels.position_pct) || 0)
      if (!Number.isFinite(position)) return '--'
      if (position < 0) return '下方'
      if (position > 100) return '上方'
      return `${position.toFixed(0)}%`
    },
    platformListText (row) {
      const levels = (row && row.platform_levels) || {}
      if (!levels.available) return '结构位待补'
      const state = String(levels.breakout_state || levels.state || '')
      const top = this.priceText(levels.attack_price)
      const distance = Number(levels.distance_to_attack_pct || 0)
      const kind = String(levels.box_kind || 'model')
      if (kind === 'dynamic_range') return `区间参考 · ${this.platformDistanceText(levels)}`
      const target = kind === 'n_breakout' ? 'N顶' : '突破位'
      const defense = kind === 'n_breakout' ? 'L2' : '防守位'
      if (/failed|跌破|失效/.test(state)) return `结构失效 · 跌破${defense} ${this.priceText(levels.defense_price)}`
      if (/extended|延伸/.test(state)) return `突破延伸 · ${this.platformDistanceText(levels)}`
      if ((/confirmed|量能确认|有效突破/.test(state)) && distance >= 0) return `有效突破 · ${target} ${top}`
      if (/retest|回踩|回到箱内/.test(state)) return `${target}回踩 · ${this.platformDistanceText(levels)}`
      if (/testing|试突破/.test(state) || distance >= 0) return `试突破 · ${target} ${top}`
      return `${target} ${top} · ${this.platformDistanceText(levels)}`
    },
    validLivePrice (value) {
      const number = Number(value)
      return Number.isFinite(number) && number > 0
    },
    dailyMoveLimit (code) {
      const normalized = String(code || '').replace(/\D/g, '').slice(-6)
      if (/^(4|8|920)/.test(normalized)) return 32
      if (/^(300|301|688|689)/.test(normalized)) return 22
      return 12
    },
    validLivePreClose (quote, code = '') {
      if (!quote || quote.change_pct_valid === false) return false
      const price = Number(quote.price)
      const previous = Number(quote.pre_close)
      const change = price > 0 && previous > 0 ? (price / previous - 1) * 100 : NaN
      const limit = this.dailyMoveLimit(code || quote.code)
      return Number.isFinite(change) && change >= -limit && change <= limit
    },
    liveChangePct (quote, row) {
      const snapshotChange = Number((row && row.change_pct) || 0)
      if (!quote || !this.validLivePrice(quote.price)) return snapshotChange
      if (this.validLivePreClose(quote, row && row.code)) {
        return (Number(quote.price) / Number(quote.pre_close) - 1) * 100
      }
      // Fall back to the dashboard's date-aware previous close only when the
      // relay explicitly marks its previous-close field invalid.
      const snapshotClose = Number((row && (row.pre_close || row.prev_close)) || 0)
      const change = snapshotClose > 0 ? (Number(quote.price) / snapshotClose - 1) * 100 : NaN
      const limit = this.dailyMoveLimit(row && row.code)
      if (Number.isFinite(change) && change >= -limit && change <= limit) return change
      return snapshotChange
    },
    scoreLabel (score) {
      if (score >= 85) return '高优先级'
      if (score >= 70) return '中优先级'
      return '观察'
    },
    isLiveActionPool (row) {
      return Boolean(row && ['stage_model', 'bullish_oversold', 'first_board_discovery'].includes(row.pool_id))
    },
    priceText (value) {
      const number = Number(value)
      return number > 0 ? number.toFixed(number >= 100 ? 2 : 3) : '--'
    },
    dailyMaTouched (key) {
      const position = this.selectedDailyPositions
      const movingAverage = Number(position[key] || 0)
      const intradayLow = Number(position.intraday_low || 0)
      return Boolean(
        position.available &&
        movingAverage > 0 &&
        intradayLow > 0 &&
        intradayLow <= movingAverage
      )
    },
    dailyMaBelow (key) {
      const position = this.selectedDailyPositions
      const movingAverage = Number(position[key] || 0)
      const currentPrice = Number(this.selectedRow && this.selectedRow.price || 0)
      return Boolean(
        this.dailyMaTouched(key) &&
        currentPrice > 0 &&
        movingAverage > 0 &&
        currentPrice < movingAverage
      )
    },
    dailyMaClass (key) {
      if (!this.dailyMaTouched(key)) return ''
      return this.dailyMaBelow(key) ? 'daily-ma-below' : 'daily-ma-touched'
    },
    signedPercent (value) {
      const number = Number(value)
      return `${number >= 0 ? '+' : ''}${number.toFixed(2)}%`
    },
    percentOrDash (value) {
      return Number(value) ? this.signedPercent(value) : '--'
    },
    unsignedPercentOrDash (value) {
      const number = Number(value)
      return Number.isFinite(number) && number > 0 ? `${number.toFixed(2)}%` : '--'
    },
    signedPointsOrDash (value) {
      if (value === null || value === undefined || value === '') return '--'
      const number = Number(value)
      if (!Number.isFinite(number)) return '--'
      return `${number >= 0 ? '+' : ''}${number.toFixed(2)}个百分点`
    },
    densityScoreText (value) {
      if (value === null || value === undefined || value === '') return '--'
      const number = Number(value)
      return Number.isFinite(number) ? `${number.toFixed(1)} / 100` : '--'
    },
    chipBandWidth (detail) {
      if (!detail || typeof detail !== 'object') return null
      const value = detail.cost_band_width_pct !== undefined
        ? detail.cost_band_width_pct
        : detail.concentration_pct
      const number = Number(value)
      return Number.isFinite(number) && number > 0 ? number : null
    },
    chipDensityScore (detail) {
      if (!detail || typeof detail !== 'object') return null
      if (detail.density_score !== null && detail.density_score !== undefined && detail.density_score !== '') {
        return detail.density_score
      }
      const width = this.chipBandWidth(detail)
      return width === null ? null : Math.max(0, Math.min(100, (1 - width / 12) * 100))
    },
    amountText (value) {
      const number = Number(value)
      if (!number) return '--'
      return number >= 100000000
        ? `${(number / 100000000).toFixed(2)}亿`
        : `${(number / 10000).toFixed(0)}万`
    },
    scoreText (value) {
      const number = Number(value)
      return number ? number.toFixed(1) : '--'
    },
    changeClass (value) {
      return Number(value) > 0 ? 'up' : Number(value) < 0 ? 'down' : 'flat'
    },
    formatSnapshot (value) {
      if (!value) return '暂无时间'
      return value.slice(5, 16).replace('-', '/')
    }
  }
}
</script>

<style>
:root {
  --ink: #141922;
  --ink-soft: #303845;
  --muted: #6d7480;
  --hairline: #d9ddd8;
  --canvas: #f5f3ee;
  --paper: #fcfbf8;
  --dark: #151c27;
  --cream: #efe5cf;
  --mint: #afd9c4;
  --coral: #b24e32;
  --yellow: #e9c764;
  --blue: #79a7c1;
  --green: #1d8b63;
  --red: #c34e47;
  --shadow: 0 16px 40px rgba(23, 31, 40, .07);
}

* { box-sizing: border-box; }
html, body, #app { min-height: 100%; margin: 0; }
body {
  color: var(--ink);
  background: var(--canvas);
  font-family: "Avenir Next", "PingFang SC", "Helvetica Neue", sans-serif;
  -webkit-font-smoothing: antialiased;
}
button, input, select { font: inherit; }
button { cursor: pointer; }

.app-shell { min-height: 100vh; }
.topbar {
  height: 72px;
  padding: 0 46px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--paper);
  border-bottom: 1px solid var(--hairline);
}
.brand-lockup, .topbar-actions, .topbar-center, .market-state { display: flex; align-items: center; }
.brand-lockup { gap: 11px; min-width: 210px; }
.brand-mark {
  display: grid;
  width: 31px; height: 31px; place-items: center;
  background: var(--dark); color: #fff;
  border-radius: 7px;
  font-family: Georgia, serif; font-size: 18px; font-weight: 700;
}
.brand-name { font-size: 13px; font-weight: 800; letter-spacing: .14em; }
.brand-caption { margin-top: 1px; color: var(--muted); font-family: Georgia, serif; font-size: 11px; font-style: italic; }
.topbar-center { gap: 15px; color: var(--muted); font-size: 11px; letter-spacing: .12em; }
.topbar-kicker { color: var(--ink); font-weight: 700; }
.topbar-divider { width: 26px; height: 1px; background: var(--hairline); }
.topbar-date { letter-spacing: .08em; }
.topbar-actions { gap: 20px; }
.market-state { gap: 8px; color: var(--muted); font-size: 12px; }
.state-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--yellow); }
.state-dot.live { background: var(--green); box-shadow: 0 0 0 4px rgba(29, 139, 99, .12); }
.icon-button {
  width: 35px; height: 35px; padding: 0;
  color: var(--ink); background: transparent;
  border: 1px solid var(--hairline); border-radius: 50%;
  font-size: 20px; line-height: 1;
}
.icon-button:hover, .ghost-button:hover, .pool-item:hover { border-color: #a4aaa5; }
.icon-button.spinning { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.dashboard { max-width: 1760px; margin: 0 auto; padding: 62px 46px 28px; }
.hero { display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 54px; }
.eyebrow { margin: 0 0 13px; color: var(--coral); font-size: 10px; font-weight: 800; letter-spacing: .18em; }
.hero h1 { margin: 0; font-family: Georgia, "Songti SC", serif; font-size: clamp(39px, 5vw, 70px); font-weight: 400; letter-spacing: -.045em; line-height: .98; }
.hero h1 em { color: var(--coral); font-style: normal; }
.hero-copy { max-width: 475px; margin: 24px 0 0; color: var(--muted); font-family: Georgia, "Songti SC", serif; font-size: 16px; line-height: 1.7; }
.hero-aside { width: 190px; padding: 18px 0 2px 22px; border-left: 1px solid var(--hairline); }
.hero-aside-label { color: var(--muted); font-size: 11px; letter-spacing: .12em; }
.hero-aside-value { margin-top: 9px; font-size: 15px; font-weight: 700; line-height: 1.4; }
.hero-aside-meta { margin-top: 7px; color: var(--muted); font-size: 11px; }

.metric-strip { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 56px; }
.metric-card { min-height: 145px; padding: 22px 24px; border-radius: 11px; }
.metric-dark { color: #fff; background: var(--dark); }
.metric-cream { background: var(--cream); }
.metric-mint { background: var(--mint); }
.metric-coral { color: #fff; background: var(--coral); }
.metric-label { font-size: 11px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; opacity: .72; }
.metric-value { margin-top: 20px; font-family: Georgia, serif; font-size: 42px; line-height: .9; }
.metric-foot { margin-top: 21px; font-size: 11px; opacity: .7; }

.opportunity-radar { margin-bottom: 52px; padding: 27px; background: var(--paper); border: 1px solid var(--hairline); border-radius: 12px; box-shadow: var(--shadow); }
.opportunity-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 25px; }
.opportunity-head h2 { margin: 0; font-family: Georgia, "Songti SC", serif; font-size: 28px; font-weight: 400; }
.opportunity-head p:not(.eyebrow) { max-width: 620px; margin: 10px 0 0; color: var(--muted); font-size: 11px; line-height: 1.6; }
.opportunity-market { min-width: 150px; padding-left: 22px; border-left: 1px solid var(--hairline); text-align: right; }
.opportunity-market span, .opportunity-market small { display: block; color: var(--muted); font-size: 10px; }
.opportunity-market strong { display: block; margin: 7px 0 3px; color: var(--coral); font-family: Georgia, serif; font-size: 31px; font-weight: 400; }
.opportunity-warning { margin-top: 18px; padding: 9px 11px; color: #78572a; background: #f5ead0; border: 1px solid #e5cf9f; border-radius: 5px; font-size: 10px; }
.opportunity-summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; margin-top: 23px; overflow: hidden; background: var(--hairline); border: 1px solid var(--hairline); border-radius: 7px; }
.opportunity-summary div { padding: 13px 15px; background: #f7f5ef; }
.opportunity-summary span { display: block; color: var(--muted); font-size: 9px; }
.opportunity-summary strong { display: block; margin-top: 6px; font-family: Georgia, serif; font-size: 20px; font-weight: 400; }
.opportunity-evaluation { display: flex; flex-wrap: wrap; gap: 8px 18px; align-items: center; margin-top: 12px; color: var(--muted); font-size: 9px; }
.opportunity-evaluation > span { color: var(--ink); font-weight: 700; letter-spacing: .06em; }
.opportunity-evaluation small { font-size: 9px; }
.opportunity-body { display: grid; grid-template-columns: .78fr 1.5fr; gap: 25px; margin-top: 25px; }
.opportunity-section-title { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 9px; }
.opportunity-section-title span { font-size: 11px; font-weight: 700; letter-spacing: .08em; }
.opportunity-section-title small { color: var(--muted); font-size: 9px; }
.opportunity-board-card, .opportunity-stock-row { border-bottom: 1px solid #e8e6df; }
.opportunity-board-card { display: flex; width: 100%; align-items: center; justify-content: space-between; min-height: 53px; gap: 15px; padding: 0 7px; color: var(--ink); background: transparent; border-top: 0; border-right: 0; border-left: 0; text-align: left; }
.opportunity-board-card:hover { background: #f7f3ea; }
.opportunity-board-card.active { background: #edf5ef; box-shadow: inset 3px 0 0 var(--green); }
.opportunity-board-card > div:first-child { min-width: 0; }
.opportunity-board-card strong { display: block; overflow: hidden; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.opportunity-board-card small { display: block; margin-top: 4px; color: var(--muted); font-size: 9px; }
.opportunity-board-score { flex: 0 0 auto; text-align: right; }
.opportunity-board-score strong { color: var(--coral); font-family: Georgia, serif; font-size: 16px; font-weight: 400; }
.opportunity-board-score small { max-width: 130px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.opportunity-clear-board { padding: 3px 7px; color: var(--green); background: transparent; border: 1px solid rgba(29, 139, 99, .35); border-radius: 4px; font-size: 9px; }
.opportunity-stock-row { display: grid; width: 100%; min-height: 53px; grid-template-columns: minmax(150px, 1fr) 66px 48px 125px; gap: 10px; align-items: center; padding: 0 4px; color: var(--ink); background: transparent; border-top: 0; border-right: 0; border-left: 0; text-align: left; }
.opportunity-stock-row:hover { background: #f7f3ea; }
.opportunity-stock-name { min-width: 0; }
.opportunity-stock-name strong, .opportunity-stock-name small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.opportunity-stock-name strong { font-size: 12px; }
.opportunity-stock-name small { margin-top: 4px; color: var(--muted); font-size: 9px; }
.opportunity-stock-row > .up, .opportunity-stock-row > .down, .opportunity-stock-row > .flat, .opportunity-score { text-align: right; font-family: Georgia, serif; font-size: 12px; }
.opportunity-score { color: var(--coral); }
.opportunity-score strong, .opportunity-score small { display: block; }
.opportunity-score strong { font-size: 15px; font-weight: 400; }
.opportunity-score small { margin-top: 2px; color: var(--muted); font-family: inherit; font-size: 8px; white-space: nowrap; }
.opportunity-state { padding: 5px 7px; border-radius: 4px; font-size: 9px; text-align: center; white-space: nowrap; }
.opportunity-strong { color: #fff; background: var(--green); }
.opportunity-focus { color: #15573f; background: #cfe9d9; }
.opportunity-wait { color: #735a20; background: #f3e8c7; }
.opportunity-invalid { color: #8a3a31; background: #f4d6cc; }
.opportunity-empty { padding: 25px 0; color: var(--muted); border-top: 1px solid #e8e6df; font-size: 10px; }

.decision-overview { margin-bottom: 34px; padding: 22px; background: var(--paper); border: 1px solid var(--hairline); border-radius: 8px; box-shadow: var(--shadow); }
.decision-overview-head { display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.decision-overview-head .eyebrow { margin-bottom: 7px; }
.decision-overview-head h1 { margin: 0; font-size: 26px; font-weight: 600; letter-spacing: 0; line-height: 1.2; }
.view-mode-switch { display: grid; grid-template-columns: repeat(2, 1fr); width: 142px; padding: 3px; background: #e9e8e3; border: 1px solid var(--hairline); border-radius: 7px; }
.view-mode-switch button { min-height: 34px; padding: 0 12px; color: var(--muted); background: transparent; border: 0; border-radius: 5px; font-size: 12px; }
.view-mode-switch button.active { color: #fff; background: var(--dark); }
.decision-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-top: 20px; border: 1px solid var(--hairline); border-radius: 7px; overflow: hidden; }
.decision-metrics article { min-width: 0; min-height: 94px; padding: 16px; background: #f7f6f2; border-right: 1px solid var(--hairline); }
.decision-metrics article:last-child { border-right: 0; }
.decision-metrics span, .decision-metrics small { display: block; overflow: hidden; color: var(--muted); font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.decision-metrics strong { display: block; overflow: hidden; margin: 10px 0 7px; font-size: 21px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.decision-metrics .decision-positive { background: #edf6f0; }
.decision-metrics .decision-positive strong { color: var(--green); }
.decision-metrics .decision-risk { background: #f8efec; }
.decision-metrics .decision-risk strong { color: var(--red); }
.calibration-strip { display: flex; flex-wrap: wrap; gap: 8px 18px; align-items: center; margin-top: 12px; color: var(--muted); font-size: 9px; }
.calibration-strip > span { color: var(--ink); font-weight: 700; }
.compact-warning { margin-top: 12px; }

.workspace { display: grid; grid-template-columns: 210px minmax(620px, 1fr) minmax(340px, 400px); gap: 24px; align-items: start; }
.pool-rail { position: sticky; top: 20px; max-height: calc(100vh - 40px); overflow-y: auto; padding-top: 4px; }
.rail-heading { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
.rail-heading h2 { margin: 0; font-family: Georgia, serif; font-size: 25px; font-weight: 400; }
.rail-count { min-width: 26px; padding: 4px 7px; color: var(--muted); background: #e3e2dd; border-radius: 10px; font-size: 11px; text-align: center; }
.pool-item {
  width: 100%; min-height: 66px; padding: 9px 10px;
  display: grid; grid-template-columns: 28px minmax(0, 1fr) auto; gap: 9px; align-items: center;
  color: var(--ink); background: transparent; border: 1px solid transparent; border-radius: 8px;
  text-align: left; transition: background .15s ease, border-color .15s ease;
}
.pool-item.active { background: var(--paper); border-color: var(--hairline); box-shadow: var(--shadow); }
.pool-item.stale { opacity: .6; }
.pool-item-mark { width: 27px; height: 27px; display: grid; place-items: center; border-radius: 6px; color: var(--ink); background: var(--cream); font-size: 16px; }
.mark-all { color: #fff; background: var(--dark); }
.mark-core_action { color: #fff; background: var(--green); }
.mark-conditional_watch { color: #614d1d; background: #eadcae; }
.mark-risk_review { color: #fff; background: var(--red); }
.mark-auction_dragon { background: #d6c5e4; }
.mark-trend_start { background: var(--mint); }
.mark-oversold_reversal { background: #f2c7aa; }
.mark-stage_model { background: var(--yellow); }
.mark-bullish_oversold { background: #f4d39c; }
.mark-first_board_discovery { color: #173b32; background: #a9d7c5; }
.pool-item-copy { min-width: 0; }
.pool-item-copy strong { display: block; overflow: hidden; font-size: 12px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.pool-item-copy small { display: block; margin-top: 4px; overflow: hidden; color: var(--muted); font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.pool-item-count { color: var(--muted); font-family: Georgia, serif; font-size: 14px; }
.rail-note { display: flex; gap: 10px; margin: 28px 12px 0 9px; color: var(--muted); }
.note-line { flex: 0 0 1px; background: var(--coral); }
.rail-note p { margin: -3px 0 0; font-family: Georgia, serif; font-size: 12px; line-height: 1.6; }

.pool-view { min-width: 0; }
.view-header { display: flex; justify-content: space-between; gap: 22px; align-items: flex-end; padding: 0 0 16px; }
.view-kicker { display: flex; align-items: center; gap: 8px; color: var(--coral); font-size: 11px; font-weight: 800; letter-spacing: .1em; }
.active-pool-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--coral); }
.view-header h2 { margin: 9px 0 0; font-family: Georgia, serif; font-size: 26px; font-weight: 400; }
.view-controls { display: flex; gap: 8px; align-items: center; }
.search-box { width: 180px; height: 36px; display: flex; align-items: center; gap: 8px; padding: 0 11px; color: var(--muted); background: var(--paper); border: 1px solid var(--hairline); border-radius: 6px; }
.search-box input { width: 100%; border: 0; outline: 0; color: var(--ink); background: transparent; font-size: 12px; }
.ghost-button { height: 36px; padding: 0 11px; color: var(--ink-soft); background: transparent; border: 1px solid var(--hairline); border-radius: 6px; font-size: 11px; }
.ghost-button.selected { color: var(--green); background: rgba(175, 217, 196, .4); border-color: var(--green); }
.filter-glyph { margin-right: 4px; font-size: 15px; }
.refresh-control { height: 36px; display: flex; align-items: center; gap: 7px; padding-left: 6px; color: var(--muted); font-size: 10px; white-space: nowrap; }
.refresh-control select { padding: 7px 5px; color: var(--ink); background: var(--paper); border: 1px solid var(--hairline); border-radius: 5px; outline: 0; font-size: 11px; }
.subtabs { display: flex; gap: 4px; overflow-x: auto; padding: 0 0 12px; border-bottom: 1px solid var(--hairline); }
.subtab-group { display: flex; flex: 0 0 auto; align-items: center; gap: 2px; padding-left: 8px; border-left: 1px solid var(--hairline); }
.subtab-group-label { padding: 0 4px; color: #8a918c; font-size: 9px; white-space: nowrap; }
.subtab { padding: 8px 12px; color: var(--muted); background: transparent; border: 0; border-radius: 5px; font-size: 12px; white-space: nowrap; }
.subtab span { margin-left: 3px; color: #9a9f9b; font-family: Georgia, serif; font-size: 11px; }
.subtab span[title] { font-family: inherit; }
.subtab.active { color: #fff; background: var(--dark); }
.subtab.active span { color: #cbd1d4; }
.count-legend { min-height: 14px; margin: -5px 0 10px; color: #8a918c; font-size: 9px; line-height: 1.5; }

.notice-bar { display: flex; align-items: center; gap: 10px; margin: 14px 0 -2px; padding: 10px 13px; border-radius: 7px; }
.error-notice { color: #72342d; background: #f0d4c9; }
.confirmation-notice { color: #66501e; background: #f4ead0; border: 1px solid #e2c98d; }
.notice-bar > span { display: grid; width: 22px; height: 22px; place-items: center; color: #fff; background: var(--coral); border-radius: 50%; font-weight: 700; }
.confirmation-notice > span { background: #b69032; }
.notice-bar strong { font-size: 12px; }
.notice-bar p { margin: 3px 0 0; color: #8b5b51; font-size: 11px; }
.confirmation-notice p { color: #796332; }
.notice-bar button { margin-left: auto; padding: 5px 9px; color: #72342d; background: transparent; border: 1px solid rgba(114, 52, 45, .35); border-radius: 4px; font-size: 11px; }

.table-card { max-height: min(74vh, 900px); overflow: auto; margin-top: 14px; background: var(--paper); border: 1px solid var(--hairline); border-radius: 10px; box-shadow: var(--shadow); }
.table-head, .stock-row { display: grid; grid-template-columns: 42px minmax(180px, 1.3fr) 112px 75px minmax(145px, 1fr) 78px; gap: 14px; align-items: center; }
.table-head { position: sticky; top: 0; z-index: 4; min-height: 38px; padding: 0 18px; color: var(--muted); background: #f0efea; border-bottom: 1px solid var(--hairline); font-size: 10px; letter-spacing: .08em; text-transform: uppercase; }
.head-rank { padding-left: 3px; }
.head-price, .head-score, .head-action { text-align: right; }
.head-price { display: flex; justify-content: flex-end; gap: 3px; }
.head-score { display: flex; justify-content: flex-end; }
.sort-button {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 3px;
  color: var(--muted);
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  font-size: 10px;
  letter-spacing: .04em;
  white-space: nowrap;
}
.sort-button:hover { color: var(--ink); background: #e6e5df; }
.sort-button.active { color: var(--ink); border-color: var(--hairline); }
.sort-indicator { width: 9px; color: var(--coral); font-size: 11px; line-height: 1; }
.stock-row { width: 100%; min-height: 70px; padding: 10px 18px; color: var(--ink); background: transparent; border: 0; border-bottom: 1px solid #e9e8e3; text-align: left; }
.stock-row:last-of-type { border-bottom: 0; }
.stock-row:hover, .stock-row.selected { background: #f7f3ea; }
.row-rank { color: #9b9f9b; font-family: Georgia, serif; font-size: 13px; }
.stock-identity { min-width: 0; }
.stock-name-line { display: flex; align-items: center; gap: 6px; }
.stock-name-line strong { overflow: hidden; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
.stock-identity small, .score-cell small, .signal-cell small { display: block; margin-top: 5px; overflow: hidden; color: var(--muted); font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.stock-identity .evidence-summary { color: #858c92; font-size: 9px; }
.role-badge { flex: 0 0 auto; max-width: 74px; overflow: hidden; padding: 3px 5px; border: 1px solid transparent; border-radius: 4px; font-size: 8px; line-height: 1; text-overflow: ellipsis; white-space: nowrap; }
.role-core { color: #155b41; background: #d8eee1; border-color: #9ecdb1; }
.role-front { color: #66501e; background: #f3e7bd; border-color: #dbc67f; }
.role-follow { color: #626970; background: #e7e9e7; border-color: #ced2cf; }
.role-pending { color: #596876; background: #e1eaee; border-color: #c4d2d8; }
.ma-cross-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
.ma-cross-badge { display: inline-block; padding: 3px 5px; border: 1px solid transparent; border-radius: 4px; font-size: 9px; line-height: 1; white-space: nowrap; }
.ma-cross-active { color: #4d6672; background: #dce9ed; border-color: #b8d0d8; }
.ma-cross-confirmed { color: #176044; background: #d7ede0; border-color: #9ccdae; }
.ma-cross-intraday { color: #795814; background: #f5e6b6; border-color: #dcc36e; box-shadow: 0 0 0 1px rgba(220, 195, 110, .18); }
.action-tier-badge { display: inline-block; margin-top: 7px; padding: 3px 6px; border: 1px solid transparent; border-radius: 4px; font-size: 10px; line-height: 1; white-space: nowrap; }
.tier-confirmed-attention { color: #14563c; background: #cfe9d9; border-color: #86c29e; }
.tier-early-wash { color: #6f4e0c; background: #f7e7aa; border-color: #d9bd58; }
.tier-tdx-channel { color: #155066; background: #d9f1ee; border-color: #86bbb8; }
.tier-channel-up { color: #176044; background: #d7ede0; border-color: #9ccdae; }
.tier-channel-down { color: #8a3a31; background: #f5ddd6; border-color: #dfaaa0; }
.tier-chip-peak { color: #694b1d; background: #f4e7c7; border-color: #d5ba81; }
.tier-chip-trend-t5 { color: #214f63; background: #dbecef; border-color: #93bcc5; }
.tier-chip-trend-ready { color: #14563c; background: #cfe9d9; border-color: #73b48d; box-shadow: 0 0 0 1px rgba(115, 180, 141, .2); }
.tier-bullish-oversold { color: #734715; background: #f7dfb9; border-color: #d8a85d; }
.tier-bullish-oversold-ready { color: #0f5942; background: #c8ead9; border-color: #61b18d; box-shadow: 0 0 0 1px rgba(97, 177, 141, .24); }
.tier-wait { color: #735a20; background: #f3e8c7; border-color: #d8c58a; }
.tier-structure { color: #4d5961; background: #e7e9e7; border-color: #c9cecb; }
.tier-no-chase { color: #854b2b; background: #f5dfd0; border-color: #e1b9a1; }
.tier-long { color: #4f6672; background: #dce9ed; border-color: #b8d0d8; }
.tier-risk { color: #8a3a31; background: #f4d6cc; border-color: #dfaaa0; }
.tier-neutral { color: #56616b; background: #e6e8e5; border-color: #ccd0cd; }
.live-badge { padding: 2px 4px; color: var(--green); background: rgba(175, 217, 196, .42); border-radius: 3px; font-size: 8px; letter-spacing: .06em; }
.l2-row-badge { padding: 2px 5px; border: 1px solid transparent; border-radius: 3px; font-size: 8px; letter-spacing: .03em; }
.l2-row-badge.l2-confirm { color: #0f5942; background: #d3ecdf; border-color: #86c4a7; }
.l2-row-badge.l2-conflict { color: #8a3a31; background: #f5d9d2; border-color: #dfaaa0; }
.stock-price { text-align: right; }
.stock-price strong { display: block; font-family: Georgia, serif; font-size: 15px; font-weight: 500; }
.stock-price span { display: block; margin-top: 5px; font-family: Georgia, serif; font-size: 12px; }
.up { color: var(--green); }
.down { color: var(--red); }
.flat { color: var(--muted); }
.score-cell { text-align: right; }
.score-cell strong { font-family: Georgia, serif; font-size: 16px; font-weight: 500; }
.score-cell small { color: var(--coral); }
.signal-cell { min-width: 0; }
.sector-name { display: block; overflow: hidden; color: var(--ink-soft); font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.support-signal { display: block; margin-top: 5px; overflow: hidden; font-size: 9px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.box-row-signal { display: block; margin-top: 4px; overflow: hidden; font-size: 9px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.box-row-signal.box-confirmed { color: #247253; }
.box-row-signal.box-testing { color: #8a6814; }
.box-row-signal.box-failed { color: var(--red); }
.box-row-signal.box-inside { color: #66747d; }
.support-positive { color: var(--green); }
.support-caution { color: #8a6814; }
.support-negative { color: var(--red); }
.support-muted { color: var(--muted); }
.row-status { display: flex; align-items: center; justify-content: flex-end; gap: 7px; }
.status-chip { padding: 5px 7px; border-radius: 4px; font-size: 10px; white-space: nowrap; }
.status-confirmed { color: #166646; background: #d7ede0; }
.status-positive { color: #7a641a; background: #f4e8b9; }
.status-risk { color: #8a3a31; background: #f4d6cc; }
.status-watch { color: #56616b; background: #e6e8e5; }
.row-chevron { color: #a2a6a3; font-size: 19px; line-height: 1; }
.table-foot { position: sticky; bottom: 0; z-index: 3; padding: 12px 18px; color: var(--muted); background: rgba(251, 250, 246, .96); border-top: 1px solid #e9e8e3; font-size: 10px; backdrop-filter: blur(8px); }
.loading-state, .empty-state { padding: 65px 20px; color: var(--muted); text-align: center; }
.empty-symbol { color: var(--coral); font-family: Georgia, serif; font-size: 33px; }
.empty-state h3 { margin: 12px 0 6px; color: var(--ink); font-family: Georgia, serif; font-size: 20px; font-weight: 400; }
.empty-state p { margin: 0; font-size: 12px; }

.insight-grid { position: sticky; top: 20px; display: grid; max-height: calc(100vh - 40px); grid-template-columns: 1fr; gap: 14px; align-self: start; overflow-y: auto; padding-right: 3px; scrollbar-width: thin; scrollbar-color: rgba(122, 128, 124, .45) transparent; }
.insight-card { min-height: 250px; padding: 24px; border-radius: 10px; }
.detail-card { color: #fff; background: var(--dark); }
.pulse-card { background: var(--cream); }
.card-topline { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.detail-card .card-topline { position: sticky; top: -24px; z-index: 5; margin: -24px -24px 0; padding: 20px 24px 12px; background: linear-gradient(180deg, var(--dark) 78%, rgba(20, 25, 34, 0)); }
.detail-top-actions { display: flex; min-width: 0; align-items: center; justify-content: flex-end; gap: 9px; }
.detail-close { display: none; width: 32px; height: 32px; padding: 0; place-items: center; color: #fff; background: rgba(255, 255, 255, .08); border: 1px solid rgba(255, 255, 255, .16); border-radius: 50%; font-size: 22px; line-height: 1; }
.detail-backdrop { display: none; }
.detail-card .eyebrow { color: var(--mint); }
.pulse-card .eyebrow { color: var(--coral); }
.selected-pool, .pulse-time { color: rgba(255, 255, 255, .56); font-size: 10px; }
.pulse-time { color: var(--muted); }
.selected-content { display: flex; flex-direction: column; margin-top: 25px; }
.selected-title { order: 1; }
.platform-levels { order: 2; }
.l2-panel { order: 3; }
.daily-position-panel { order: 4; }
.selected-metrics { order: 5; }
.ma-cross-panel { order: 6; }
.reason-list { order: 7; }
.selected-stage-state { order: 8; }
.risk-line { order: 9; }
.stage-timeline-panel { order: 10; }
.selected-title, .pulse-title { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; }
.selected-title h3, .pulse-title h3 { margin: 0; font-family: Georgia, serif; font-size: 27px; font-weight: 400; }
.selected-title p { margin: 7px 0 0; color: rgba(255, 255, 255, .58); font-size: 11px; }
.selected-price { text-align: right; }
.selected-price strong { display: block; font-family: Georgia, serif; font-size: 25px; font-weight: 400; }
.selected-price span { display: block; margin-top: 5px; font-size: 12px; }
.selected-metrics { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 10px; margin-top: 27px; }
.selected-metrics div { padding-right: 10px; border-right: 1px solid rgba(255, 255, 255, .14); }
.selected-metrics div:nth-child(2n) { border-right: 0; }
.selected-metrics span { display: block; color: rgba(255, 255, 255, .5); font-size: 10px; }
.selected-metrics strong { display: block; margin-top: 8px; font-family: Georgia, serif; font-size: 16px; font-weight: 400; }
.decision-detail-summary { order: 2; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1px; margin-top: 21px; overflow: hidden; background: rgba(255, 255, 255, .12); border: 1px solid rgba(255, 255, 255, .12); border-radius: 6px; }
.decision-detail-summary div { min-width: 0; padding: 11px; background: rgba(255, 255, 255, .055); }
.decision-detail-summary span { display: block; color: rgba(255, 255, 255, .48); font-size: 9px; }
.decision-detail-summary strong { display: block; margin-top: 6px; overflow-wrap: anywhere; font-size: 11px; font-weight: 500; line-height: 1.45; }
.decision-detail-summary .decision-invalid { grid-column: 1 / -1; }
.decision-detail-summary .decision-invalid strong { color: #ffc1b5; }
.support-strip { order: 3; margin-top: 12px; padding: 13px; background: rgba(255, 255, 255, .045); border: 1px solid rgba(255, 255, 255, .13); border-radius: 6px; }
.support-strip.support-positive { background: rgba(58, 151, 105, .09); border-color: rgba(112, 205, 158, .38); }
.support-strip.support-caution { background: rgba(220, 174, 74, .08); border-color: rgba(230, 185, 78, .35); }
.support-strip.support-negative { background: rgba(195, 78, 71, .10); border-color: rgba(221, 115, 104, .46); }
.support-strip-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.support-strip-head span { display: block; color: rgba(255, 255, 255, .54); font-size: 9px; }
.support-strip-head small { display: block; margin-top: 4px; color: rgba(255, 255, 255, .4); font-size: 8px; }
.support-strip-head strong { color: #fff; font-size: 12px; font-weight: 600; text-align: right; }
.support-strip.support-positive .support-strip-head strong { color: #b9efd2; }
.support-strip.support-caution .support-strip-head strong { color: #ffe2a0; }
.support-strip.support-negative .support-strip-head strong { color: #ffc1b5; }
.support-strip-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 1px; margin-top: 11px; overflow: hidden; background: rgba(255, 255, 255, .09); border-radius: 4px; }
.support-strip-grid div { min-width: 0; padding: 8px 7px; background: rgba(8, 12, 18, .34); }
.support-strip-grid span { display: block; color: rgba(255, 255, 255, .4); font-size: 8px; }
.support-strip-grid strong { display: block; margin-top: 5px; overflow: hidden; color: #fff; font-family: Georgia, serif; font-size: 12px; font-weight: 400; text-overflow: ellipsis; white-space: nowrap; }
.support-strip-updated { display: block; margin-top: 8px; color: rgba(255, 255, 255, .34); font-size: 8px; }
.evidence-matrix { order: 4; display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.evidence-matrix span { padding: 5px 7px; color: rgba(255, 255, 255, .55); background: rgba(255, 255, 255, .07); border: 1px solid rgba(255, 255, 255, .12); border-radius: 4px; font-size: 9px; }
.evidence-matrix span.passed { color: #b9efd2; background: rgba(58, 151, 105, .13); border-color: rgba(112, 205, 158, .38); }
.evidence-matrix span.unknown { color: #ffe2a0; background: rgba(220, 174, 74, .1); border-color: rgba(230, 185, 78, .32); }
.selected-content .platform-levels { order: 5; }
.selected-content .l2-panel { order: 6; }
.selected-content .daily-position-panel { order: 7; }
.selected-content .selected-metrics { order: 8; }
.selected-content .ma-cross-panel { order: 9; }
.selected-content .reason-list { order: 10; }
.selected-content .selected-stage-state { order: 11; }
.selected-content .risk-line { order: 12; }
.selected-content .stage-timeline-panel { order: 13; }
.l2-panel { margin-top: 22px; padding: 16px; background: rgba(255, 255, 255, .055); border: 1px solid rgba(255, 255, 255, .14); border-radius: 7px; }
.l2-panel.l2-strong-support { background: rgba(58, 151, 105, .18); border-color: rgba(112, 205, 158, .72); }
.l2-panel.l2-support { background: rgba(58, 151, 105, .10); border-color: rgba(112, 205, 158, .46); }
.l2-panel.l2-weak-support { background: rgba(220, 174, 74, .12); border-color: rgba(230, 185, 78, .56); }
.l2-panel.l2-pressure { background: rgba(195, 78, 71, .16); border-color: rgba(221, 115, 104, .72); }
.l2-panel.l2-confirm { background: rgba(58, 151, 105, .12); border-color: rgba(112, 205, 158, .55); }
.l2-panel.l2-conflict { background: rgba(195, 78, 71, .12); border-color: rgba(221, 115, 104, .65); }
.l2-panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.l2-panel-head span { display: block; color: var(--mint); font-size: 11px; letter-spacing: .04em; }
.l2-panel-head small { display: block; margin-top: 5px; color: rgba(255, 255, 255, .42); font-size: 9px; }
.l2-panel-head strong { color: #fff; font-size: 13px; font-weight: 500; }
.l2-confirm .l2-panel-head strong { color: #b9efd2; }
.l2-conflict .l2-panel-head strong { color: #ffc1b5; }
.l2-strong-support .l2-panel-head strong, .l2-support .l2-panel-head strong { color: #b9efd2; }
.l2-weak-support .l2-panel-head strong { color: #ffe2a0; }
.l2-pressure .l2-panel-head strong { color: #ffc1b5; }
.l2-stage-note { margin: 11px 0 0; color: rgba(255, 255, 255, .6); font-size: 10px; line-height: 1.5; }
.l2-factor-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; margin-top: 13px; }
.l2-factor-grid div { padding: 9px; background: rgba(0, 0, 0, .13); border-radius: 4px; }
.l2-factor-grid span { display: block; color: rgba(255, 255, 255, .43); font-size: 9px; }
.l2-factor-grid strong { display: block; margin-top: 6px; font-family: Georgia, serif; font-size: 14px; font-weight: 400; }
.l2-components { display: block; margin-top: 10px; color: rgba(255, 255, 255, .52); font-size: 9px; line-height: 1.5; }
.l2-streams { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.l2-streams span { padding: 4px 6px; color: rgba(255, 255, 255, .45); background: rgba(255, 255, 255, .06); border-radius: 3px; font-size: 8px; }
.l2-streams span.online { color: #c6efd5; background: rgba(93, 181, 132, .16); }
.l2-streams span.failed { color: #ffc1b5; background: rgba(195, 78, 71, .17); }
.l2-error, .l2-updated { display: block; margin-top: 10px; color: #f0b09c; font-size: 9px; }
.l2-updated { color: rgba(255, 255, 255, .38); }
.daily-position-panel { margin-top: 22px; padding-top: 17px; border-top: 1px solid rgba(255, 255, 255, .14); }
.daily-position-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.daily-position-head span { color: var(--mint); font-size: 11px; letter-spacing: .04em; }
.daily-position-head small { color: rgba(255, 255, 255, .46); font-size: 9px; }
.daily-ma-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 11px; }
.daily-ma-item { position: relative; padding: 10px 8px; background: rgba(255, 255, 255, .06); border: 1px solid rgba(255, 255, 255, .1); border-radius: 5px; }
.daily-ma-item.daily-ma-touched { background: rgba(241, 193, 89, .2); border-color: #e6b94e; box-shadow: inset 0 0 0 1px rgba(230, 185, 78, .18); }
.daily-ma-item.daily-ma-below { background: rgba(224, 111, 93, .2); border-color: #e07b69; box-shadow: inset 0 0 0 1px rgba(224, 123, 105, .2); }
.daily-ma-item span, .daily-range span { display: block; color: rgba(255, 255, 255, .5); font-size: 9px; }
.daily-ma-item strong { display: block; margin-top: 7px; color: #fff; font-family: Georgia, serif; font-size: 15px; font-weight: 400; }
.daily-ma-item.daily-ma-touched strong { color: #ffe2a0; }
.daily-ma-item.daily-ma-below strong { color: #ffc1b4; }
.daily-ma-item small { display: block; min-height: 11px; margin-top: 6px; color: #ffe2a0; font-size: 8px; line-height: 1.3; }
.daily-ma-item.daily-ma-below small { color: #ffc1b4; }
.daily-range { display: grid; grid-template-columns: repeat(2, 1fr) auto; gap: 10px; align-items: end; margin-top: 10px; }
.daily-range > div { padding: 10px; background: rgba(175, 217, 196, .08); border: 1px solid rgba(175, 217, 196, .14); border-radius: 5px; }
.daily-range strong { display: block; margin-top: 7px; color: #fff; font-family: Georgia, serif; font-size: 16px; font-weight: 400; }
.daily-range small { padding-bottom: 10px; color: rgba(255, 255, 255, .4); font-size: 9px; white-space: nowrap; }
.ma-cross-panel { margin-top: 22px; padding-top: 17px; border-top: 1px solid rgba(255, 255, 255, .14); }
.ma-cross-panel-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.ma-cross-panel-head span { color: var(--mint); font-size: 11px; letter-spacing: .04em; }
.ma-cross-panel-head small { color: rgba(255, 255, 255, .46); font-size: 9px; }
.ma-cross-detail-list { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 11px; }
.ma-cross-detail { min-width: 145px; padding: 10px; border: 1px solid rgba(255, 255, 255, .14); border-radius: 5px; }
.ma-cross-detail strong { display: block; font-family: Georgia, serif; font-size: 15px; font-weight: 400; }
.ma-cross-detail span { display: block; margin-top: 5px; color: rgba(255, 255, 255, .72); font-size: 10px; }
.ma-cross-detail small { display: block; margin-top: 6px; color: rgba(255, 255, 255, .42); font-size: 9px; }
.ma-cross-detail.ma-cross-active { background: rgba(184, 208, 216, .12); border-color: rgba(184, 208, 216, .6); }
.ma-cross-detail.ma-cross-confirmed { background: rgba(156, 205, 174, .12); border-color: rgba(156, 205, 174, .7); }
.ma-cross-detail.ma-cross-intraday { background: rgba(220, 195, 110, .14); border-color: rgba(220, 195, 110, .85); }
.ma-cross-detail.ma-cross-intraday strong { color: #ffe2a0; }
.ma-cross-detail.ma-cross-confirmed strong { color: #c6efd5; }
.ma-cross-detail.ma-cross-active strong { color: #c8e0e7; }
.ma-cross-empty { margin-top: 11px; color: rgba(255, 255, 255, .42); font-size: 10px; }
.reason-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 23px; }
.reason-list span { padding: 5px 7px; color: #d9e8de; background: rgba(175, 217, 196, .14); border: 1px solid rgba(175, 217, 196, .2); border-radius: 4px; font-size: 10px; }
.stage-timeline-panel { margin-top: 20px; padding-top: 15px; border-top: 1px solid rgba(255, 255, 255, .14); }
.stage-timeline-head, .stage-cycle-heading { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 8px; }
.stage-timeline-head strong { color: var(--mint); font-size: 11px; font-weight: 500; }
.stage-timeline-head span, .stage-cycle-heading span { color: rgba(255, 255, 255, .45); font-size: 9px; }
.stage-timeline-message, .stage-timeline-note { color: rgba(255, 255, 255, .48); font-size: 9px; line-height: 1.5; }
.stage-cycle-list { margin-top: 10px; }
.stage-cycle + .stage-cycle { margin-top: 13px; padding-top: 12px; border-top: 1px solid rgba(255, 255, 255, .1); }
.stage-cycle-heading strong { font-size: 10px; font-weight: 500; }
.stage-phase-row { display: grid; grid-template-columns: minmax(90px, .8fr) minmax(150px, 1.2fr); gap: 5px 12px; padding: 8px 0; border-bottom: 1px solid rgba(255, 255, 255, .07); }
.stage-phase-row > strong { grid-row: span 2; color: #e5eee8; font-size: 10px; font-weight: 500; }
.stage-phase-row > span, .stage-phase-row > small { color: rgba(255, 255, 255, .55); font-size: 9px; line-height: 1.4; }
.stage-phase-row > small { grid-column: 2; color: rgba(255, 255, 255, .38); }
.platform-levels { margin-top: 18px; padding: 15px 0 0; border-top: 1px solid rgba(255, 255, 255, .14); }
.platform-levels-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.platform-levels-head span { color: var(--mint); font-size: 11px; letter-spacing: .04em; }
.platform-levels-head small { color: rgba(255, 255, 255, .46); font-size: 9px; text-align: right; }
.platform-state-line { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-top: 12px; }
.platform-state-line strong { color: #fff; font-size: 12px; font-weight: 600; }
.platform-state-line span { color: rgba(255, 255, 255, .56); font-size: 9px; text-align: right; }
.platform-levels.box-confirmed .platform-state-line strong { color: #b9efd2; }
.platform-levels.box-testing .platform-state-line strong { color: #ffe2a0; }
.platform-levels.box-failed .platform-state-line strong { color: #ffc1b5; }
.platform-track { position: relative; height: 18px; margin: 9px 2px 3px; }
.platform-track-fill { position: absolute; top: 8px; right: 0; left: 0; height: 2px; background: rgba(255, 255, 255, .24); }
.platform-track-fill::before, .platform-track-fill::after { position: absolute; top: -3px; width: 1px; height: 8px; content: ''; background: rgba(255, 255, 255, .48); }
.platform-track-fill::before { left: 0; }
.platform-track-fill::after { right: 0; }
.platform-track-marker { position: absolute; top: 4px; width: 10px; height: 10px; margin-left: -5px; background: #fff; border: 2px solid var(--dark); border-radius: 50%; box-shadow: 0 0 0 1px rgba(255, 255, 255, .48); }
.platform-levels.box-confirmed .platform-track-marker { background: #8ed6b1; }
.platform-levels.box-testing .platform-track-marker { background: #e6b94e; }
.platform-levels.box-failed .platform-track-marker { background: #df7a6d; }
.platform-level-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); margin-top: 3px; border-top: 1px solid rgba(255, 255, 255, .1); border-bottom: 1px solid rgba(255, 255, 255, .1); }
.platform-level { min-width: 0; padding: 10px 8px; }
.platform-level + .platform-level { border-left: 1px solid rgba(255, 255, 255, .1); }
.current-level { text-align: center; }
.attack-level { text-align: right; }
.platform-level-label { color: rgba(255, 255, 255, .64); font-size: 10px; }
.platform-level strong { display: block; margin-top: 6px; overflow: hidden; font-family: Georgia, serif; font-size: 18px; font-weight: 400; text-overflow: ellipsis; white-space: nowrap; }
.platform-level small { display: block; margin-top: 5px; overflow: hidden; color: rgba(255, 255, 255, .48); font-size: 8px; text-overflow: ellipsis; white-space: nowrap; }
.platform-structure-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 1px; margin-top: 8px; overflow: hidden; background: rgba(255, 255, 255, .1); border: 1px solid rgba(255, 255, 255, .1); border-radius: 4px; }
.platform-structure-metrics span { min-width: 0; padding: 7px 6px; color: rgba(255, 255, 255, .42); background: rgba(255, 255, 255, .025); font-size: 8px; white-space: nowrap; }
.platform-structure-metrics strong { display: block; margin-top: 4px; overflow: hidden; color: rgba(255, 255, 255, .78); font-size: 9px; font-weight: 500; text-overflow: ellipsis; }
.platform-rules { display: flex; flex-wrap: wrap; gap: 5px 12px; margin-top: 9px; color: rgba(255, 255, 255, .42); font-size: 8px; line-height: 1.45; }
.platform-rules span:first-child { color: rgba(185, 239, 210, .68); }
.stage-tracking-badge { display: inline-block; max-width: 170px; margin-left: 5px; padding: 2px 5px; border: 1px solid transparent; border-radius: 3px; font-size: 9px; line-height: 1.25; vertical-align: middle; }
.stage-tracking-badge.stage-state-watch { color: #3f5f20; background: #e5efd5; border-color: #b7cf92; }
.stage-tracking-badge.stage-state-breakout { color: #745000; background: #fff0bd; border-color: #d9b84f; }
.stage-tracking-badge.stage-state-risk { color: #8a2f26; background: #f8dcd5; border-color: #dd9d91; }
.stage-tracking-badge.stage-state-neutral { color: #4d5961; background: #e7e9e7; border-color: #c9cecb; }
.selected-stage-state { display: flex; align-items: baseline; flex-wrap: wrap; gap: 8px; margin-top: 16px; padding: 10px 12px; border: 1px solid rgba(255, 255, 255, .14); border-radius: 5px; }
.selected-stage-state > span { color: rgba(255, 255, 255, .54); font-size: 10px; }
.selected-stage-state > strong { font-size: 11px; font-weight: 500; }
.selected-stage-state > small { flex-basis: 100%; color: rgba(255, 255, 255, .42); font-size: 9px; }
.selected-stage-state.stage-state-watch { color: #d9e6bd; background: rgba(159, 190, 118, .1); border-color: rgba(159, 190, 118, .45); }
.selected-stage-state.stage-state-breakout { color: #ffe0a1; background: rgba(220, 174, 74, .11); border-color: rgba(220, 174, 74, .65); }
.selected-stage-state.stage-state-risk { color: #ffc1b5; background: rgba(195, 78, 71, .11); border-color: rgba(221, 115, 104, .7); }
.selected-stage-state.stage-state-neutral { color: rgba(255, 255, 255, .65); background: rgba(255, 255, 255, .08); border-color: rgba(255, 255, 255, .18); }
.risk-line { margin-top: 15px; color: #f0b09c; font-size: 10px; line-height: 1.5; }
.placeholder-detail { display: flex; min-height: 185px; flex-direction: column; align-items: center; justify-content: center; color: rgba(255, 255, 255, .58); text-align: center; }
.placeholder-mark { color: var(--mint); font-family: Georgia, serif; font-size: 35px; }
.placeholder-detail h3 { margin: 9px 0 5px; color: #fff; font-family: Georgia, serif; font-size: 20px; font-weight: 400; }
.placeholder-detail p { max-width: 240px; margin: 0; font-size: 11px; line-height: 1.6; }
.pulse-title { margin-top: 24px; }
.pulse-title h3 { color: var(--ink); }
.pulse-badge { padding: 6px 8px; color: #166646; background: var(--mint); border-radius: 4px; font-family: Georgia, serif; font-size: 13px; }
.bar-list { margin-top: 23px; }
.bar-item + .bar-item { margin-top: 13px; }
.bar-label { display: flex; justify-content: space-between; color: var(--muted); font-size: 10px; }
.bar-label strong { color: var(--ink); font-family: Georgia, serif; font-size: 12px; font-weight: 400; }
.bar-track { height: 5px; margin-top: 6px; overflow: hidden; background: rgba(20, 25, 34, .09); border-radius: 4px; }
.bar-track span { display: block; height: 100%; border-radius: inherit; }
.bar-mint { background: var(--green); }
.bar-blue { background: var(--blue); }
.bar-yellow { background: #c6a323; }
.bar-coral { background: var(--coral); }
.pulse-note { margin: 20px 0 0; color: var(--muted); font-family: Georgia, serif; font-size: 11px; line-height: 1.5; }
.fqgate-radar { margin-top: 22px; padding-top: 16px; border-top: 1px solid var(--hairline); }
.fqgate-radar-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.fqgate-radar-head span { display: block; color: var(--coral); font-size: 9px; letter-spacing: .08em; }
.fqgate-radar-head small { display: block; margin-top: 5px; color: var(--muted); font-size: 9px; }
.fqgate-radar-head > strong { color: var(--muted); font-family: ui-monospace, monospace; font-size: 10px; font-weight: 400; }
.fqgate-overview-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; margin-top: 12px; overflow: hidden; background: var(--hairline); border: 1px solid var(--hairline); border-radius: 5px; }
.fqgate-overview-grid div { padding: 8px 7px; background: rgba(255, 255, 255, .42); }
.fqgate-overview-grid span { display: block; color: var(--muted); font-size: 8px; }
.fqgate-overview-grid strong { display: block; margin-top: 5px; color: var(--ink); font-family: Georgia, serif; font-size: 14px; font-weight: 400; }
.fqgate-alert-list { margin-top: 10px; }
.fqgate-alert-row { display: grid; grid-template-columns: 48px minmax(0, 1fr); gap: 7px; padding: 7px 0; border-bottom: 1px solid rgba(20, 25, 34, .08); }
.fqgate-alert-row span { color: var(--muted); font-family: ui-monospace, monospace; font-size: 8px; }
.fqgate-alert-row strong { overflow: hidden; color: var(--ink); font-size: 9px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.fqgate-empty { display: block; margin-top: 10px; color: var(--muted); font-size: 9px; }
.dashboard-footer { display: flex; justify-content: space-between; gap: 15px; margin-top: 40px; padding-top: 17px; color: var(--muted); border-top: 1px solid var(--hairline); font-size: 10px; letter-spacing: .06em; }

@media (max-width: 1500px) {
  .view-header { align-items: flex-start; flex-direction: column; }
  .view-controls { width: 100%; justify-content: flex-end; }
}

@media (max-width: 1280px) {
  .workspace { grid-template-columns: 220px minmax(0, 1fr); }
  .insight-grid {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 70;
    width: min(460px, calc(100vw - 32px));
    height: 100dvh;
    max-height: none;
    display: block;
    overflow-y: auto;
    padding: 16px;
    background: #eeede7;
    box-shadow: -18px 0 48px rgba(20, 25, 34, .22);
    transform: translateX(105%);
    transition: transform .22s ease;
  }
  .insight-grid.detail-open { transform: translateX(0); }
  .detail-backdrop {
    position: fixed;
    inset: 0;
    z-index: 69;
    display: block;
    padding: 0;
    background: rgba(20, 25, 34, .4);
    border: 0;
    backdrop-filter: blur(2px);
  }
  .detail-close { display: grid; }
  .detail-card { min-height: calc(100dvh - 32px); }
  .pulse-card { display: none; }
}

@media (max-width: 1100px) {
  .topbar { padding: 0 24px; }
  .dashboard { padding: 45px 24px 26px; }
  .topbar-center { display: none; }
  .table-head, .stock-row { grid-template-columns: 34px minmax(160px, 1.2fr) 100px 66px minmax(120px, 1fr) 68px; gap: 10px; padding-left: 13px; padding-right: 13px; }
}
@media (max-width: 820px) {
  .decision-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .decision-metrics article:nth-child(2) { border-right: 0; }
  .decision-metrics article:nth-child(-n + 2) { border-bottom: 1px solid var(--hairline); }
  .workspace { grid-template-columns: 1fr; gap: 24px; }
  .daily-range { grid-template-columns: repeat(2, 1fr); }
  .daily-range small { grid-column: 1 / -1; padding: 0; }
  .pool-rail {
    position: sticky;
    top: 0;
    z-index: 12;
    display: flex;
    max-height: none;
    gap: 7px;
    overflow-x: auto;
    margin: 0 -24px;
    padding: 10px 24px;
    background: rgba(238, 237, 231, .95);
    border-top: 1px solid var(--hairline);
    border-bottom: 1px solid var(--hairline);
    backdrop-filter: blur(10px);
    scrollbar-width: none;
  }
  .pool-rail::-webkit-scrollbar { display: none; }
  .rail-heading, .rail-note { display: none; }
  .pool-item { flex: 0 0 172px; min-height: 58px; background: var(--paper); border-color: var(--hairline); }
  .view-header { align-items: flex-start; flex-direction: column; }
  .view-controls { width: 100%; flex-wrap: wrap; }
}
@media (max-width: 600px) {
  .topbar { height: 62px; padding: 0 16px; }
  .brand-lockup { min-width: 0; }
  .market-state { display: none; }
  .dashboard { padding: 24px 16px 20px; }
  .decision-overview { margin-bottom: 24px; padding: 16px; }
  .decision-overview-head { align-items: stretch; flex-direction: column; gap: 14px; }
  .decision-overview-head h1 { font-size: 22px; }
  .view-mode-switch { width: 100%; }
  .decision-metrics { margin-top: 16px; }
  .decision-metrics article { min-height: 86px; padding: 13px; }
  .decision-metrics strong { margin: 8px 0 6px; font-size: 18px; white-space: normal; }
  .decision-metrics span, .decision-metrics small { white-space: normal; }
  .calibration-strip { align-items: flex-start; flex-direction: column; gap: 5px; }
  .pool-rail { margin-right: -16px; margin-left: -16px; padding-right: 16px; padding-left: 16px; }
  .pool-item { flex-basis: 158px; }
  .view-controls { align-items: stretch; flex-direction: column; }
  .search-box { width: 100%; }
  .refresh-control { justify-content: space-between; padding: 0; }
  .table-card { max-height: 72dvh; overflow-x: hidden; }
  .table-head { display: none; }
  .stock-row {
    min-width: 0;
    min-height: 0;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px 12px;
    padding: 14px;
  }
  .row-rank { display: none; }
  .stock-identity { grid-column: 1; grid-row: 1 / span 2; }
  .stock-price { grid-column: 2; grid-row: 1; }
  .score-cell { grid-column: 2; grid-row: 2; }
  .signal-cell { grid-column: 1 / -1; grid-row: 3; padding-top: 8px; border-top: 1px solid #ece9e1; }
  .row-status { grid-column: 1 / -1; grid-row: 4; justify-content: space-between; }
  .sector-name { white-space: normal; }
  .stock-identity small, .score-cell small, .signal-cell small { white-space: normal; }
  .table-foot { font-size: 9px; line-height: 1.5; }
  .insight-grid { width: 100vw; padding: 0; background: var(--dark); }
  .insight-card { padding: 19px; }
  .detail-card { min-height: 100dvh; padding: 20px 18px 30px; border-radius: 0; }
  .detail-card .card-topline { top: -20px; margin: -20px -18px 0; padding: 16px 18px 12px; }
  .decision-detail-summary { grid-template-columns: 1fr; }
  .decision-detail-summary .decision-invalid { grid-column: 1; }
  .support-strip-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); }
  .selected-metrics { grid-template-columns: repeat(2, 1fr); row-gap: 15px; }
  .l2-panel { padding: 14px; }
  .l2-factor-grid { grid-template-columns: repeat(2, 1fr); }
  .platform-levels-head { align-items: flex-start; flex-direction: column; }
  .platform-levels-head small { text-align: left; }
  .platform-structure-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .dashboard-footer { align-items: flex-start; flex-direction: column; }
}
</style>
